import React, { useState, useEffect, useRef, useMemo } from 'react';
import uPlot from 'uplot';
import 'uplot/dist/uPlot.min.css';
import { createPortal } from 'react-dom';
import SunCalc from 'suncalc';
import { useI18n } from '../i18n/I18nProvider';
import binService from '../services/binService';
import siteService from '../services/siteService';
import { MapContainer, TileLayer, Marker, Popup, Tooltip, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { getCachedThresholds, subscribeThresholds, refreshThresholds } from '../services/settingsService';
import { startRealtime, on as onRealtime, stopRealtime } from '../services/realtime';
import Modal from '../components/Modal';
import { debouncedGeocode, debouncedGeocodeMulti, debouncedGeocodeMultiNotable, debouncedGeocodeMultiSmart } from '../services/geocodeClient';
import SignalIcon from '../components/icons/SignalIcon';

function classifyByRssi(val) {
  if (val >= -70) return 'excellent';
  if (val >= -85) return 'good';
  return 'weak';
}

function buildSignalFromRssi(raw) {
  if (raw == null) return null;
  const rssi = Number(raw);
  if (isNaN(rssi)) return null;
  return { quality: classifyByRssi(rssi), rssi, csq: null };
}

function toEpochSeconds(ts) {
  if (!ts) return null;
  const d = new Date(ts);
  const ms = d.getTime();
  if (isNaN(ms)) return null;
  return Math.floor(ms / 1000);
}

function buildUPlotOptions({ series, yLabel, zoomHooks, yRange }) {
  const axisStyle = { stroke: '#666', grid: { stroke: 'rgba(0,0,0,0.05)' } };
  return {
    height: 200,
    scales: {
      x: { time: true },
      y: yRange ? { range: yRange } : {}
    },
    axes: [
      axisStyle,
      { ...axisStyle, label: yLabel }
    ],
    legend: { show: false },
    cursor: { drag: { x: true, y: false }, points: { show: false } },
    select: { show: true },
    series: [{}, ...series],
    hooks: zoomHooks
  };
}

const UPlotChart = ({ data, options, height = 200 }) => {
  const containerRef = useRef(null);
  const plotRef = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const width = Math.max(320, Math.floor(el.clientWidth || 320));
    const plot = new uPlot({ ...options, width, height }, data, el);
    plotRef.current = plot;

    const onDblClick = () => plot.setScale('x', { min: null, max: null });
    plot.root.addEventListener('dblclick', onDblClick);

    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(entries => {
        if (!entries.length) return;
        const nextWidth = Math.max(320, Math.floor(entries[0].contentRect.width));
        if (nextWidth !== plot.width) plot.setSize({ width: nextWidth, height });
      });
      ro.observe(el);
    }

    return () => {
      if (ro) ro.disconnect();
      plot.root.removeEventListener('dblclick', onDblClick);
      plot.destroy();
      plotRef.current = null;
    };
  }, [options, height]);

  useEffect(() => {
    if (plotRef.current) plotRef.current.setData(data);
  }, [data]);

  return <div ref={containerRef} />;
};

// Custom color-coded bin icons using configurable fill thresholds
const iconCache = {};
let lastMarkerScale = 1; // persist across unmounts
const ICON_VERSION = 'v6';
const BASE_ICON_SIZE = [36, 45]; // width, height when scale = 1
function getBinIcon(fill, scale = 1, latchOpen=false, batteryLow=false) {
  let color = 'gray';
  if (typeof fill === 'number') {
    const th = getCachedThresholds();
    const g = typeof th.fill_green_threshold_pct === 'number' ? th.fill_green_threshold_pct : 70;
    const r = typeof th.fill_red_threshold_pct === 'number' ? th.fill_red_threshold_pct : 90;
    // If yellow explicitly set, use it; else derive between green/red
    const y = typeof th.fill_yellow_threshold_pct === 'number' ? th.fill_yellow_threshold_pct : r;
    if (fill < g) color = 'green';
    else if (fill < y) color = 'gold';
    else color = 'red';
  }
  const scaleKey = Math.round(scale * 100); // e.g. 50..100
  // Include latch state in cache key so locked/unlocked icons don't collide
  const cacheKey = `${color}-${ICON_VERSION}-${scaleKey}-l${latchOpen?1:0}-b${batteryLow?1:0}`;
  if (iconCache[cacheKey]) return iconCache[cacheKey];
  // Restored detailed shaded container icon (smaller ~12% than previous 40x50 size)
  const fillColor = color === 'gold' ? '#f9cf2d' : color === 'green' ? '#39b84a' : color === 'red' ? '#ff3d41' : '#a7aeb4';
  const rimColor = '#d9dee2';
  const sideShade = '#2a333b';
  // Lock overlay (top-right) indicating latch state
  const lockColor = latchOpen ? '#2e7d32' : '#c62828';
  const lockBody = latchOpen ? '#43a047' : '#e53935';
  // Slightly larger, more visible lock (top-right)
  // Doubled size lock for higher visibility
  const lockSvg = `<g transform="translate(26,0) scale(1.2)">
    <rect x="2" y="10" width="20" height="16" rx="3" ry="3" fill="${lockBody}" stroke="#18202a" stroke-width="2"/>
    <path d="M6 10 V6 a6 6 0 0 1 12 0 v4" fill="none" stroke="${lockColor}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
    <circle cx="12" cy="18" r="3" fill="#fff" stroke="#18202a" stroke-width="1.5"/>
  </g>`;
  const batterySvg = batteryLow ? `<g transform="translate(2,32) scale(1.15)">
    <rect x="0" y="8" width="24" height="16" rx="3" ry="3" fill="#ff9800" stroke="#18202a" stroke-width="2"/>
    <rect x="4" y="6" width="5" height="3" fill="#ff9800" stroke="#18202a" stroke-width="1.8"/>
    <rect x="15" y="6" width="5" height="3" fill="#ff9800" stroke="#18202a" stroke-width="1.8"/>
    <!-- Minus centered in left half -->
    <rect x="2.5" y="15" width="7" height="3" fill="#18202a" rx="1" />
    <!-- Plus centered and symmetric (equal left/right & above/below) -->
    <rect x="12" y="15" width="10" height="3" fill="#18202a" rx="1" />
    <rect x="15.5" y="12" width="3" height="9" fill="#18202a" rx="1" />
  </g>` : '';
  const svg = encodeURIComponent(`<?xml version="1.0" encoding="UTF-8"?>
<svg width="54" height="64" viewBox="0 0 54 64" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="bodyGrad" x1="0" x2="0" y1="0" y2="1">
      <stop offset="0%" stop-color="${fillColor}" stop-opacity="0.95"/>
      <stop offset="55%" stop-color="${fillColor}" stop-opacity="0.92"/>
      <stop offset="100%" stop-color="${fillColor}" stop-opacity="0.88"/>
    </linearGradient>
    <linearGradient id="rimGrad" x1="0" x2="0" y1="0" y2="1">
      <stop offset="0%" stop-color="${rimColor}"/>
      <stop offset="100%" stop-color="#bfc5c9"/>
    </linearGradient>
    <filter id="drop" x="-40%" y="-40%" width="180%" height="200%">
      <feDropShadow dx="0" dy="2" stdDeviation="2.2" flood-color="#000" flood-opacity="0.55" />
    </filter>
  </defs>
  <g filter="url(#drop)">
    <path d="M10 14 L44 14 L44 56 C44 58.2 42.2 60 40 60 L14 60 C11.8 60 10 58.2 10 56 Z" fill="url(#bodyGrad)" stroke="#18202a" stroke-width="2.2" />
    <g stroke="rgba(255,255,255,0.35)" stroke-width="1.3" stroke-linecap="round">
      <line x1="18" y1="18" x2="18" y2="54" />
      <line x1="26" y1="18" x2="26" y2="54" />
      <line x1="34" y1="18" x2="34" y2="54" />
    </g>
    <path d="M44 14 L44 56 C44 58.2 42.2 60 40 60 L14 60 C12.9 59.4 12.2 58.7 11.5 57.5 21.5 54 32.5 43 39 26 40.6 21.8 42 18 44 14Z" fill="rgba(0,0,0,0.15)" />
  </g>
  <path d="M8 10 C8 8.3 9.3 7 11 7 L43 7 C44.7 7 46 8.3 46 10 L46 14 L8 14 Z" fill="url(#rimGrad)" stroke="#18202a" stroke-width="2.2" />
  <path d="M8 12 L46 12" stroke="${sideShade}" stroke-width="1.4" stroke-linecap="round" />
  <rect x="11" y="12" width="30" height="4" rx="1.2" fill="#232d34" />
  ${lockSvg}
  ${batterySvg}
</svg>`);

  const w = Math.max(16, Math.round(BASE_ICON_SIZE[0] * scale));
  const h = Math.max(20, Math.round(BASE_ICON_SIZE[1] * scale));
  const icon = L.icon({
    iconUrl: `data:image/svg+xml,${svg}`,
    iconSize: [w, h],
    iconAnchor: [w / 2, h - 2],
    popupAnchor: [0, -(h - 5)],
    className: 'bin-marker-icon'
  });
  iconCache[cacheKey] = icon;
  return icon;
}

// Zoom-aware markers layer regenerating icons per zoom (no DOM transform flicker)
// Site (depot) / recycling icon cache (dark green circular arrows)
const siteIconCache = {};
function getSiteIcon(scale=1){
  const key = 'siteRecycle-' + Math.round(scale*100);
  if(siteIconCache[key]) return siteIconCache[key];
  const body = '#155a2c';
  const accent = '#1f7a3c';
  const arrow = '#ffffff';
  const svg = encodeURIComponent(`<?xml version="1.0" encoding="UTF-8"?>
<svg width="56" height="56" viewBox="0 0 56 56" xmlns="http://www.w3.org/2000/svg">
 <defs>
  <linearGradient id="sg" x1="0" y1="0" x2="0" y2="1">
    <stop offset="0%" stop-color="${accent}" />
    <stop offset="100%" stop-color="${body}" />
  </linearGradient>
  <filter id="sDrop" x="-40%" y="-40%" width="180%" height="200%">
    <feDropShadow dx="0" dy="2" stdDeviation="2.2" flood-color="#000" flood-opacity="0.45" />
  </filter>
 </defs>
 <g filter="url(#sDrop)">
  <circle cx="28" cy="28" r="20" fill="url(#sg)" stroke="#0d3c1c" stroke-width="2" />
  <g transform="translate(28 28) scale(0.7) translate(-28 -28)" fill="${arrow}" stroke="${arrow}" stroke-width="1.4" stroke-linejoin="round" stroke-linecap="round">
    <path d="M28 15 l5 8h-4a9 9 0 0 1 7 10 l-3 -2a6 6 0 0 0 -6 -8h-4l5 -8z" />
    <path d="M28 41 l-5 -8h4a9 9 0 0 1 -7 -10 l3 2a6 6 0 0 0 6 8h4l-5 8z" />
  </g>
 </g>
</svg>`);
  const size = Math.round(40 * scale);
  const icon = L.icon({ iconUrl:`data:image/svg+xml,${svg}`, iconSize:[size,size], iconAnchor:[size/2,size/2], popupAnchor:[0,-(size/2 - 6)], className:'site-marker-icon'});
  siteIconCache[key]=icon; return icon;
}

function MarkersLayer({ bins, onSelect, sites, filters, showAll }) {
  const map = useMap();
  const { t } = useI18n();
  const [lowV, setLowV] = useState(Number(getCachedThresholds().battery_low_threshold_v || 11));
  useEffect(() => {
    // Ensure we have latest thresholds and react to changes
    refreshThresholds().catch(()=>{});
    const unsub = subscribeThresholds((th) => setLowV(Number(th.battery_low_threshold_v || 11)));
    return () => { unsub && unsub(); };
  }, []);
  const [zoom, setZoom] = useState(map.getZoom());
  const hasZoomedRef = React.useRef(false);
  useEffect(() => {
    const handler = () => {
      hasZoomedRef.current = true;
      setZoom(map.getZoom());
    };
    map.on('zoomend', handler);
    return () => { map.off('zoomend', handler); };
  }, [map]);
  const minZ = 10, maxZ = 16;
  let computedScale = 1;
  if (zoom <= minZ) computedScale = 0.5; else if (zoom >= maxZ) computedScale = 1; else computedScale = 0.5 + ((zoom - minZ)/(maxZ - minZ))*0.5;
  // If the user has not actively zoomed yet (fresh mount or after tab switch), keep full size
  // unless we have a previously stored scale from an earlier zoom interaction.
  const scale = hasZoomedRef.current ? computedScale : lastMarkerScale || 1;
  lastMarkerScale = scale; // remember for next mount
  return (
    <>
      {/* Site markers (behind bins for clarity) */}
      {sites.filter(s => (s.latitude != null && s.longitude != null) || (s.location && s.location.coordinates)).map(s => {
        const lat = s.latitude != null ? s.latitude : s.location.coordinates[1];
        const lng = s.longitude != null ? s.longitude : s.location.coordinates[0];
        const siteIcon = getSiteIcon(scale * 0.85);
        const iconSize = siteIcon.options.iconSize || [32,38];
        const tooltipOffset = [Math.round(iconSize[0]/2)+10, 0];
        return (
          <Marker key={'site-'+s.site_id} position={[lat,lng]} icon={siteIcon} interactive={true}>
            <Tooltip direction="right" offset={tooltipOffset} opacity={0.95} className="site-hover-tooltip tooltip-right">
              <div style={{ fontSize:12, fontWeight:600 }}>{s.site_code}</div>
              <div style={{ fontSize:11 }}>{s.site_name}</div>
              {s.address && <div style={{ fontSize:10, opacity:.7 }}>{s.address}</div>}
            </Tooltip>
          </Marker>
        );
      })}
  {bins.filter(b => !b.is_deleted).filter(b => {
        if (showAll || !filters || filters.size===0) return true;
        const fill = b.latest_telemetry?.fill_level;
        let cat = 'nodata';
        if (typeof fill === 'number') {
          const th = getCachedThresholds();
          const g = typeof th.fill_green_threshold_pct === 'number' ? th.fill_green_threshold_pct : 70;
          const r = typeof th.fill_red_threshold_pct === 'number' ? th.fill_red_threshold_pct : 90;
          const y = typeof th.fill_yellow_threshold_pct === 'number' ? th.fill_yellow_threshold_pct : r;
          if (fill < g) cat = 'low'; else if (fill < y) cat = 'med'; else cat = 'high';
        }
        return filters.has(cat);
      }).map(b => {
        if (!(b.location && b.location.coordinates)) return null;
        const fill = b.latest_telemetry?.fill_level;
        const doorStatus = b.latest_telemetry?.door_status || b.snapshot?.door_status;
        const battery = b.latest_telemetry?.battery_level;
        // Heartbeat time: prefer heartbeat metric time, else generic time
        const hbTime = b.latest_telemetry?.time || b.latest_telemetry?.last_heartbeat || b.snapshot?.time;
        // Ajtó állapot: Nyitva/Zárva (ne legyen mindig '-')
        const doorLabel = (() => {
          const tOpen = t('bins.latch.open') || 'Nyitva';
          const tClosed = t('bins.latch.closed') || 'Zárva';
          if (b.latest_telemetry?.latch_state != null) return b.latest_telemetry.latch_state === 0 ? tOpen : tClosed;
          const ds = doorStatus;
          if (!ds) return '—';
          const mainState = ds.main || ds.state || '';
          const latchField = ds.latch || '';
          const isOpen = /open/i.test(mainState) || /unlocked|open/i.test(latchField);
          return isOpen ? tOpen : tClosed;
        })();
        // Jel minőség label
        const signalRssi = b.latest_telemetry?.rssi ?? b.snapshot?.rssi ?? b.latest_telemetry?.signal?.rssi ?? b.snapshot?.signal?.rssi;
        const signalObj = buildSignalFromRssi(signalRssi);
        const signalLabel = (() => {
          const q = signalObj?.quality;
          if (q === 'excellent') return t('bins.details.stats.signalExcellent') || 'Kiváló';
          if (q === 'good') return t('bins.details.stats.signalGood') || 'Jó';
          if (q === 'weak') return t('bins.details.stats.signalWeak') || 'Gyenge';
          return '—';
        })();
        const batteryDisplay = (() => {
          if (battery == null) return '—';
          const mv = Number(battery);
          if (isNaN(mv)) return '—';
          if (mv > 16800) return 'Hibás adat';
          return (mv/1000).toFixed(1) + ' V';
        })();
        const fillDisplay = typeof fill === 'number' ? fill + '%' : '—';
        const hbDisplay = formatShortTime(hbTime);
        // Derive latch state (0=open, 1=close)
        const latchOpen = (() => {
          if (b.latest_telemetry?.latch_state != null) return b.latest_telemetry.latch_state === 0;
          const ds = b.latest_telemetry?.door_status || b.snapshot?.door_status;
          if (ds) {
            const mainState = ds.main || ds.state || '';
            const latchField = ds.latch || '';
            return /open/i.test(mainState) || /unlocked|open/i.test(latchField);
          }
          return false;
        })();
        // Prepare icon and derive size for proper tooltip offset (so it does NOT cover the marker)
        const batt = b.latest_telemetry?.battery_level ?? b.snapshot?.battery_level;
        const batteryLow = (typeof batt === 'number' && !isNaN(batt) && (batt/1000) < lowV);
        const iconObj = getBinIcon(fill, scale, latchOpen, batteryLow);
  const iconSize = iconObj.options.iconSize || [36,45];
  // Tooltip to the RIGHT side of icon: horizontal offset (half width + gap), vertical centered
  const tooltipOffset = [Math.round(iconSize[0] / 2) + 12, 0];
        return (
          <Marker
            key={b.bin_id}
            position={[b.location.coordinates[1], b.location.coordinates[0]]}
            icon={iconObj}
            eventHandlers={{ click: () => onSelect(b.bin_id) }}
          >
            <Tooltip
              className="bin-hover-tooltip tooltip-right"
              direction="right"
              offset={tooltipOffset}
              opacity={1}
            >
              <div className="bin-tooltip-content">
                <div className="bt-title">{b.bin_identifier}</div>
                <div className="bt-row"><span>{t('bins.tooltip.fill')}</span><strong>{fillDisplay}</strong></div>
                <div className="bt-row"><span>{t('bins.tooltip.door')}</span><strong>{doorLabel}</strong></div>
                <div className="bt-row"><span>{t('bins.tooltip.battery')}</span><strong>{batteryDisplay}</strong></div>
                <div className="bt-row"><span>{t('bins.details.stats.signal')}</span><strong>{signalLabel}</strong></div>
                <div className="bt-row"><span>{t('bins.tooltip.heartbeat')}</span><strong>{hbDisplay}</strong></div>
              </div>
            </Tooltip>
            <Popup>
              <div style={{ fontSize: 13, lineHeight: 1.4 }}>
                <button
                  type="button"
                  onClick={() => onSelect(b.bin_id)}
                  style={{ background: 'none', border: 'none', padding: 0, margin: 0, color: '#1976d2', textDecoration: 'underline', cursor: 'pointer', fontWeight: 600 }}
                >{b.bin_identifier}</button>
                <br />
                <span style={{ opacity: .75 }}>{typeof fill === 'number' ? fill + '%' : ''}</span>
              </div>
            </Popup>
          </Marker>
        );
      })}
    </>
  );
}

// Csak EGYSZER fit-el (amikor először vannak koordinátás pontok).
// Utána soha nem nyúl a zoom/pan-hez, így sem a 10s-es polling,
// sem a realtime telemetria-frissítés nem ugrasztja vissza a térképet —
// akkor sem, ha a user rég belezoomolt egy adott kukára.
const FitBounds = ({ bins }) => {
  const map = useMap();
  const hasFittedRef = useRef(false);

  useEffect(() => {
    if (hasFittedRef.current) return; // már fitteltünk egyszer, a user nézete szent

    const active = bins.filter(b => !b.is_deleted);
    const source = active.length > 0 ? active : bins;
    const points = source
      .filter(b => b.location && b.location.coordinates)
      .map(b => [b.location.coordinates[1], b.location.coordinates[0]]);

    if (points.length === 0) return; // várunk, amíg lesz adat

    hasFittedRef.current = true;

    if (points.length === 1) {
      map.setView(points[0], 15, { animate: true });
    } else {
      const bounds = L.latLngBounds(points);
      map.fitBounds(bounds.pad(0.15), { animate: true });
    }
  }, [bins, map]);
  return null;
};

const MapView = ({ bins, onSelect, sites, mapFillFilters, mapShowAll, setMapFillFilters, setMapShowAll, filterSiteId }) => {
  /*
    Height calculation rationale:
    Full viewport height minus the fixed header height and the internal padding/margins.
    We reserve ~32px for the tab bar + page padding (adjust as needed) in addition to header.
  */
  const mapHeight = 'calc(100vh - var(--header-height) - 32px)';
  return (
    <div style={{ display:'flex', flexDirection:'column', height: mapHeight }}>
      <div style={{ marginBottom:8, display:'flex', flexWrap:'wrap', gap:10, alignItems:'center' }}>
        <LegendOverlay
          activeFilters={mapFillFilters}
          showAll={mapShowAll}
          onToggleAll={(e)=> {
            const checked = e.target.checked;
            setMapShowAll(checked);
            if(checked) setMapFillFilters(new Set(['low','med','high','nodata']));
          }}
          onToggle={(key)=> {
            setMapFillFilters(prev => {
              const isExclusive = prev && prev.size===1 && prev.has(key);
              if(isExclusive){
                setMapShowAll(true);
                return new Set(['low','med','high','nodata']);
              }
              setMapShowAll(false);
              return new Set([key]);
            });
          }}
          layout="row"
          size="lg"
        />
      </div>
      <div style={{ flex:1, minHeight:0, position:'relative', display:'flex' }}>
      <MapContainer center={[47.4979, 19.0402]} zoom={10} style={{ flex: 1, width: '100%', border: '1px solid #ccc', borderRadius: 4 }} attributionControl={false}>
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
        {/* key={filterSiteId}: site-váltáskor új FitBounds instance jön létre,
            így ott újra fókuszál egyszer — de a sima pollingnál/realtime-nál nem nyúl a nézethez */}
        <FitBounds key={filterSiteId || 'all'} bins={bins} />
        <MarkersLayer bins={bins} onSelect={onSelect} sites={sites} filters={mapFillFilters} showAll={mapShowAll} />
      </MapContainer>
      </div>
    </div>
  );
};

// Mini map for single selected bin in Details tab
function SingleBinMap({ bin, editMode = false, draftPosition = null, onDragEnd }) {
  const { t } = useI18n();
  const coords = bin?.details?.location?.coordinates;
  const [lowV, setLowV] = useState(Number(getCachedThresholds().battery_low_threshold_v || 11));
  useEffect(() => {
    refreshThresholds().catch(()=>{});
    const unsub = subscribeThresholds((th) => setLowV(Number(th.battery_low_threshold_v || 11)));
    return () => { unsub && unsub(); };
  }, []);
  // Prefer realtime-updated snapshot or latest_telemetry over historic array first element
  const fill = (bin?.snapshot?.fill_level ?? bin?.latest_telemetry?.fill_level ?? bin?.telemetry?.[0]?.fill_level);

  const basePosition = coords ? [coords[1], coords[0]] : null; // [lat,lng]
  const position = draftPosition ? [draftPosition.lat, draftPosition.lng] : basePosition;

  if (!position) {
    return (
      <div style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', fontSize: 13, opacity: .7 }}>
        {t('bins.details.noCoordinates') || 'No coordinates'}
      </div>
    );
  }

  function SetViewOnChange({ skip }) {
    const map = useMap();
    useEffect(() => {
      // Edit módban ne ugráltassuk vissza a térképet minden dragend-nél —
      // a user épp mozgatja a markert, a nézet legyen a szeme.
      if (skip) return;
      map.setView(position, 17, { animate: true });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [position, map, skip]);
    return null;
  }

  return (
    <MapContainer
      key={bin?.details?.bin_id || 'single-bin-map'}
      center={position}
      zoom={17}
      style={{ width: '100%', height: '100%' }}
      scrollWheelZoom={false}
      dragging={true}
      doubleClickZoom={false}
      attributionControl={false}
    >
      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
      {(() => {
        const latchOpen = (() => {
          const snapshot = bin?.snapshot || bin?.latest_telemetry;
          if (snapshot?.latch_state != null) return snapshot.latch_state === 0; // 0=open
          const ds = snapshot?.door_status;
          if(ds){
            const mainState = ds.main || ds.state || '';
            const latchField = ds.latch || '';
            return /open/i.test(mainState) || /unlocked|open/i.test(latchField);
          }
          return false;
        })();
        const battMv = bin?.snapshot?.battery_level ?? bin?.latest_telemetry?.battery_level;
        const batteryLow = (() => {
          // Újonnan felvett (szürke) kukáknál ne jelenjen meg az akkumulátor ikon
          if (typeof fill !== 'number') return false;
          const mv = Number(battMv);
          if (isNaN(mv)) return false;
          if (mv <= 0) return false; // üres / alapérték ne jelöljön lemerültet
          if (mv > 16800) return false; // invalid readings should not show overlay
          return (mv / 1000) < lowV;
        })();
        return (
          <Marker
            position={position}
            icon={getBinIcon(fill, 1, latchOpen, batteryLow)}
            key={`${latchOpen ? 'o':'c'}-${batteryLow ? 'low':'ok'}-${editMode ? 'edit':'view'}`}
            draggable={editMode}
            eventHandlers={editMode ? {
              dragend: (e) => {
                const marker = e.target;
                const ll = marker.getLatLng();
                onDragEnd && onDragEnd({ lat: ll.lat, lng: ll.lng });
              }
            } : undefined}
          />
        );
      })()}
      <SetViewOnChange skip={editMode} />
    </MapContainer>
  );
}

function LegendOverlay({ activeFilters, showAll, onToggleAll, onToggle, layout='row', size='md' }){
  const { t } = useI18n();
  const th = getCachedThresholds();
  const g = typeof th.fill_green_threshold_pct === 'number' ? th.fill_green_threshold_pct : 70;
  const r = typeof th.fill_red_threshold_pct === 'number' ? th.fill_red_threshold_pct : 90;
  const scale = size==='lg' ? 1.2 : size==='sm' ? 0.9 : 1;
  const fontSize = 11 * scale;
  const padY = 4 * scale;
  const padX = 8 * scale;
  const btnBase = { display: 'inline-flex', alignItems: 'center', gap: 4*scale, fontSize, padding: padY+'px '+padX+'px', border: '1px solid #c5c5c5', borderRadius: 4, background: '#fff', cursor: 'pointer', userSelect: 'none', lineHeight:1.1 };
  const activeStyle = { background: '#1976d2', color: '#fff', borderColor: '#1863ad' };
  const colorDot = (c) => <span style={{ width: 12*scale, height: 12*scale, borderRadius: '50%', background: c, display: 'inline-block', boxShadow: '0 0 0 1px rgba(0,0,0,.25)' }} />;
  const wrapStyle = { background: 'rgba(255,255,255,0.95)', padding: (6*scale)+'px '+(10*scale)+'px', border: '1px solid #ccc', borderRadius: 6, boxShadow: '0 2px 4px rgba(0,0,0,0.15)', display:'flex', gap:8*scale, flexWrap:'wrap', alignItems:'center' };
  const btn = (key, label, color) => {
    const active = showAll || activeFilters?.has(key);
    return <button key={key} type="button" onClick={()=> onToggle(key)} style={{ ...btnBase, ...(active ? activeStyle : {}) }}>{colorDot(color)}<span>{label}</span></button>;
  };
  return (
    <div style={wrapStyle}>
      <label style={{ display:'inline-flex', alignItems:'center', fontSize, gap:4*scale, fontWeight:600 }}>
        <input type="checkbox" checked={showAll} onChange={onToggleAll} /> {t('common.all') || 'Összes'}
      </label>
      {btn('low', (t('bins.mapLegend.low') || '< 70%').replace('70', String(g)), '#3cbf4b')}
      {btn('med', (t('bins.mapLegend.medium') || '70–89%').replace('70', String(g)).replace('89', String(r-1)), '#ffd74a')}
      {btn('high', (t('bins.mapLegend.high') || '≥ 90%').replace('90', String(r)), '#ff4d4f')}
      {btn('nodata', t('bins.mapLegend.noData'), '#9e9e9e')}
    </div>
  );
}

// Removed legacy ListView component that duplicated listing and inline details.

const BinsPage = () => {
  const [bins, setBins] = useState([]);
  // Add Bin form state
  const [newIdentifier, setNewIdentifier] = useState('');
  const [newLat, setNewLat] = useState('');
  const [newLng, setNewLng] = useState('');
  const [newAddress, setNewAddress] = useState('');
  const [geocoding, setGeocoding] = useState(false);
  const [createSuggestions, setCreateSuggestions] = useState([]); // multi geocode results
  const [createGeoStatus, setCreateGeoStatus] = useState('idle');
  const [suppressBinCreateSuggestions, setSuppressBinCreateSuggestions] = useState(false); // avoid late async overwrite after selection
  const [suppressBinEditSuggestions, setSuppressBinEditSuggestions] = useState(false); // avoid late async overwrite after selecting edit suggestion
  const [hideDeleted, setHideDeleted] = useState(true);
  const [filterSiteId, setFilterSiteId] = useState(''); // '' = összes
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [sites, setSites] = useState([]);
  const [siteLoading, setSiteLoading] = useState(false);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  // State related to adding a new bin removed with legacy list form
  const [activeTab, setActiveTab] = useState('map'); // 'map' | 'list' | 'details'
  const [selectedBinId, setSelectedBinId] = useState(null);
  const [selectedBin, setSelectedBin] = useState(null); // full details (includes snapshot/latest_telemetry)
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState('');
  const [manualCoords, setManualCoords] = useState(false);
  const [innerTab, setInnerTab] = useState('details'); // simplified: details | chart
  const [chartTab, setChartTab] = useState('compression'); // compression | empty | battery | temperature | signal
  const [chartRange, setChartRange] = useState('day'); // 'day' | 'week' | 'month'
  const telemetryCache = useRef(new Map()); // cache: `${binId}:${range}:${metric}` → telemetry rows
  const [confirmState, setConfirmState] = useState({ open:false, mode:null, bin:null, loading:false });
  // Inline edit state for bins list
  const [editingBinId, setEditingBinId] = useState(null);
  const [editValues, setEditValues] = useState({ bin_identifier:'', site_id:'', latitude:'', longitude:'', address:'' });
  const [editError, setEditError] = useState('');
  const [liveGeo, setLiveGeo] = useState(null); // first suggested
  const [geoStatus, setGeoStatus] = useState('idle'); // idle|loading|ok|fail
  const [editSuggestions, setEditSuggestions] = useState([]); // multi for inline edit
  const [geoVersion, setGeoVersion] = useState(0); // trigger map re-render
  const [infoMessage, setInfoMessage] = useState('');
  // Inline geo suggestion portal state
  const addressInputRef = useRef(null);
  const [suggestPos, setSuggestPos] = useState(null); // {top,left,width}
  const [highlightIdx, setHighlightIdx] = useState(-1);
  // Ordering mode state
  const [orderingMode, setOrderingMode] = useState(false);
  const [pendingOrder, setPendingOrder] = useState([]); // array of bin_ids in clicked order
  const [savingOrder, setSavingOrder] = useState(false);
  const [orderError, setOrderError] = useState('');
  // Single-column sorting state for list view
  const [sortField, setSortField] = useState(null); // 'state' | 'fill' | 'battery'
  const [sortDir, setSortDir] = useState('asc'); // 'asc' | 'desc'
  // Latch command sending state
  const [latchSending, setLatchSending] = useState(false);
  // Tracks which bin is waiting for a latch ack from the device (bin_id + fallback timeout)
  const latchAwaitingAckRef = useRef(null);
  // Collection list modal state
  const [collectionModal, setCollectionModal] = useState({ open:false, minFill:90, sort:'order', includeUnordered:false, updatedWithin:180, loading:false, siteId:'' });
  // Maintenance list modal state (battery threshold)
  const [maintenanceModal, setMaintenanceModal] = useState({ open:false, minV: Number(getCachedThresholds().battery_low_threshold_v || 11), sort:'order', includeUnordered:false, loading:false, siteId:'' });
  // Map legend filtering state (exclusive category toggle)
  const [mapFillFilters, setMapFillFilters] = useState(new Set(['low','med','high','nodata']));
  const [mapShowAll, setMapShowAll] = useState(true);
  // Details tab: manual position (drag & drop) editing state
  const [positionEditMode, setPositionEditMode] = useState(false);
  const [draftPosition, setDraftPosition] = useState(null); // { lat, lng }
  const [savingPosition, setSavingPosition] = useState(false);
  const [positionError, setPositionError] = useState('');

  // Polling merge: ne írja felül a frissebb realtime értéket
  const fetchBins = async (silent=false) => {
    try {
      const data = await binService.getAllBins();
      // Frontend biztonsági szűrés: ne jelenjen meg 'all' fantom kuka akkor sem, ha backend valamiért visszaadná
      const filtered = Array.isArray(data) ? data.filter(b => String(b.bin_identifier).toLowerCase() !== 'all') : data;
      setBins(prev => {
        if(!prev || prev.length === 0) return filtered;
        const prevMap = new Map(prev.map(b => [b.bin_id, b]));
        return filtered.map(incoming => {
          const existing = prevMap.get(incoming.bin_id);
          if(!existing) return incoming;
          const inLt = incoming.latest_telemetry;
          const exLt = existing.latest_telemetry;
          if(exLt && inLt){
            const inTime = inLt.time ? new Date(inLt.time).getTime() : 0;
            const exTime = exLt.time ? new Date(exLt.time).getTime() : 0;
            if(exTime > inTime){
              return { ...incoming, latest_telemetry: { ...exLt } };
            }
          }
          return incoming;
        });
      });
    } catch (error) {
      if(!silent) console.error('Failed to fetch bins', error);
    }
  };

  useEffect(() => { fetchBins(); }, []);
  // Load sites for dropdown
  useEffect(() => {
    const loadSites = async () => {
      setSiteLoading(true);
      try { const data = await siteService.getSites(); setSites(data || []); if (data && data.length === 1) setSelectedSiteId(String(data[0].site_id)); } catch(e){ /* silent */ } finally { setSiteLoading(false); }
    };
    loadSites();
  }, []);

  // Realtime WebSocket integration + latency mérés + státusz
  const [rtStatus, setRtStatus] = useState('connecting');
  // UI: realtime státusz blokk megjelenítése a jobb felső sarokban (jelenleg kikapcsolva)
  const SHOW_RT_STATUS = false;
  const latencyRef = useRef({ value:null });
  const [lastLatency, setLastLatency] = useState(null);
  useEffect(() => {
    startRealtime();
  const offRtStatus = onRealtime('realtime.status', st => { setRtStatus(st.state); });
    const latencyTimer = setInterval(()=> { if(latencyRef.current.value!=null) setLastLatency(latencyRef.current.value); }, 3000);
    // telemetry.update handler
    const offTelemetry = onRealtime('telemetry.update', (ev) => {
      if (!ev || !ev.bin_id) return;
      setBins(prev => prev.map(b => {
        if (b.bin_id !== ev.bin_id) return b;
        const lt = { ...(b.latest_telemetry || {}) };
        // Map metrics to fields
        if (ev.metric === 'fill_level') lt.fill_level = ev.value;
        else if (ev.metric === 'battery_level') lt.battery_level = ev.value;
        else if (ev.metric === 'temperature') lt.temperature = ev.value;
        else if (ev.metric === 'door_status') {
          lt.door_status = ev.value;
          // latch_state from broadcast (0=open, 1=close); fallback: derive from door_status payload
          if (ev.latch_state !== undefined) {
            lt.latch_state = ev.latch_state;
          } else {
            const mainSt = typeof ev.value === 'object' ? (ev.value?.main || ev.value?.state || '') : '';
            const latchSt = typeof ev.value === 'object' ? (ev.value?.latch || '') : '';
            lt.latch_state = (/open/i.test(mainSt) || /unlocked|open/i.test(latchSt)) ? 0 : 1;
          }
        }
        else if (ev.metric === 'latch_state') {
          lt.latch_state = ev.value; // 0=open, 1=close
          // Device acknowledged the latch command — re-enable the button
          if (latchAwaitingAckRef.current && latchAwaitingAckRef.current.bin_id === ev.bin_id) {
            clearTimeout(latchAwaitingAckRef.current.timeoutId);
            latchAwaitingAckRef.current = null;
            setLatchSending(false);
          }
        }
        else if (ev.metric === 'signal') {
          lt.signal = ev.value; // { quality, rssi, csq }
          if (ev.value && typeof ev.value.rssi === 'number') lt.rssi = ev.value.rssi;
        }
        else if (ev.metric === 'heartbeat') lt.last_heartbeat = ev.time;
        // time best-effort
        lt.time = ev.time || lt.time || new Date().toISOString();
        return { ...b, latest_telemetry: lt };
      }));
      if(ev.time){
        try { latencyRef.current.value = Date.now() - new Date(ev.time).getTime(); } catch(_){ }
      }
      // Patch selected bin snapshot if matches
      setSelectedBin(prev => {
        if (!prev || !prev.details || prev.details.bin_id !== ev.bin_id) return prev;
        const snapshot = { ...(prev.snapshot || prev.latest_telemetry || {} ) };
        if (ev.metric === 'fill_level') snapshot.fill_level = ev.value;
        else if (ev.metric === 'battery_level') snapshot.battery_level = ev.value;
        else if (ev.metric === 'temperature') snapshot.temperature = ev.value;
        else if (ev.metric === 'door_status') {
          snapshot.door_status = ev.value;
          if (ev.latch_state !== undefined) {
            snapshot.latch_state = ev.latch_state;
          } else {
            const mainSt = typeof ev.value === 'object' ? (ev.value?.main || ev.value?.state || '') : '';
            const latchSt = typeof ev.value === 'object' ? (ev.value?.latch || '') : '';
            snapshot.latch_state = (/open/i.test(mainSt) || /unlocked|open/i.test(latchSt)) ? 0 : 1;
          }
        }
        else if (ev.metric === 'latch_state') snapshot.latch_state = ev.value; // 0=open, 1=close
        else if (ev.metric === 'signal') {
          snapshot.signal = ev.value;
          if (ev.value && typeof ev.value.rssi === 'number') snapshot.rssi = ev.value.rssi;
        }
        else if (ev.metric === 'heartbeat') snapshot.time = ev.time;
        snapshot.time = ev.time || snapshot.time || new Date().toISOString();
        return { ...prev, snapshot, latest_telemetry: snapshot };
      });
    });
  const offBinStatus = onRealtime('status.update', (ev) => {
      if (!ev || !ev.bin_id) return;
      setBins(prev => prev.map(b => b.bin_id === ev.bin_id ? { ...b, status: ev.status } : b));
      setSelectedBin(prev => {
        if (!prev || !prev.details || prev.details.bin_id !== ev.bin_id) return prev;
        return { ...prev, details: { ...prev.details, status: ev.status } };
      });
    });
    return () => {
  offTelemetry();
  offBinStatus();
  offRtStatus(); // realtime.status unsub
      clearInterval(latencyTimer);
      // We deliberately do not stopRealtime globally to allow other pages reuse; could call stopRealtime if needed.
    };
  }, []);
  // Lightweight polling while on map or list to keep colors/stats fresh
  useEffect(() => {
    const active = activeTab === 'map' || activeTab === 'list';
    if (!active) return;
    const id = setInterval(()=> fetchBins(true), 10000); // 10s
    return () => clearInterval(id);
  }, [activeTab]);

  // Role detection (admin/manager can create bins)
  const currentUser = (() => {
    try { return JSON.parse(localStorage.getItem('user')); } catch(e){ return null; }
  })();
  const userRole = currentUser?.role;
  const canCreate = userRole === 'admin' || userRole === 'manager';
  const canEditPosition = userRole === 'admin' || userRole === 'manager';

  const handleCreateBin = async (e) => {
    e.preventDefault();
    if (!newIdentifier.trim()) { setCreateError(t('bins.form.errorIdentifierRequired')); return; }
    if (!selectedSiteId) { setCreateError(t('sites.validationSelectSite') || 'Select site'); return; }
    setCreating(true);
    setCreateError('');
    try {
      const payload = { bin_identifier: newIdentifier.trim(), site_id: parseInt(selectedSiteId,10) };
      if (newAddress.trim()) payload.address = newAddress.trim();
      const latNum = parseFloat(newLat.replace(',', '.'));
      const lngNum = parseFloat(newLng.replace(',', '.'));
      if (!isNaN(latNum) && !isNaN(lngNum)) {
        payload.latitude = latNum;
        payload.longitude = lngNum;
      }
      await binService.createBin(payload);
      setNewIdentifier(''); setNewLat(''); setNewLng(''); setNewAddress('');
      // Keep selected site
      fetchBins(true);
    } catch (err) {
      const apiMsg = err.response?.data?.msg || err.response?.data?.message || err.message;
      setCreateError(apiMsg);
    } finally {
      setCreating(false);
    }
  };

  const { t } = useI18n();

  const zoomHooks = useMemo(() => ({
    setSelect: [u => {
      if (u.select.width < 10) return;
      const min = u.posToVal(u.select.left, 'x');
      const max = u.posToVal(u.select.left + u.select.width, 'x');
      u.setScale('x', { min, max });
      u.setSelect({ left: 0, width: 0, top: 0, height: 0 }, false);
    }]
  }), []);

  const batteryOptions = useMemo(() => buildUPlotOptions({
    series: [
      { label: 'Feszültség (V)', stroke: '#1976d2', width: 2, points: { show: false } },
      { label: 'Hibás adat', stroke: 'rgba(198,40,40,0.9)', width: 0, points: { show: true, size: 5, stroke: '#c62828', fill: '#c62828' } },
      { label: 'Küszöb: 11 V', stroke: '#e53935', width: 1, dash: [6, 4], points: { show: false } }
    ],
    yLabel: 'Volt (V)',
    zoomHooks
  }), [zoomHooks]);

  const temperatureOptions = useMemo(() => buildUPlotOptions({
    series: [
      { label: 'Hőmérséklet (°C)', stroke: '#ef6c00', width: 2, points: { show: false } }
    ],
    yLabel: '°C',
    zoomHooks
  }), [zoomHooks]);

  const signalOptions = useMemo(() => buildUPlotOptions({
    series: [
      { label: 'Jelerősség (dBm)', stroke: '#546e7a', width: 2, points: { show: false } }
    ],
    yLabel: 'dBm',
    zoomHooks,
    yRange: (u, min, max) => [Math.min(min, -120), Math.max(max, -40)]
  }), [zoomHooks]);

  const buildSelectedBinSeed = (bin) => {
    if (!bin) return null;
    const { latest_telemetry, ...details } = bin;
    const snapshot = latest_telemetry ? { ...latest_telemetry } : null;
    return {
      details,
      snapshot,
      latest_telemetry: latest_telemetry || null,
      telemetry: []
    };
  };

  const selectBin = (id, seed) => {
    setSelectedBinId(id);
    setSelectedBin(seed || null);
    setDetailsError('');
    setDetailsLoading(false);
    setActiveTab('details');
  };

  const selectBinById = (id) => {
    const seed = buildSelectedBinSeed(bins.find(b => b.bin_id === id));
    selectBin(id, seed);
  };

  const chartMetric = chartTab === 'battery'
    ? 'battery'
    : chartTab === 'temperature'
      ? 'temperature'
      : chartTab === 'signal'
        ? 'signal'
        : null;

  useEffect(() => {
    if (!selectedBinId) {
      setSelectedBin(null);
      setDetailsError('');
      setDetailsLoading(false);
    }
  }, [selectedBinId]);

  // Reset position edit state whenever the selected bin changes
  useEffect(() => {
    setPositionEditMode(false);
    setDraftPosition(null);
    setPositionError('');
    setSavingPosition(false);
  }, [selectedBinId]);

  // Load details when selectedBinId changes (detailsOnly=true: skips telemetry history, very fast)
  useEffect(() => {
    if (!selectedBinId || innerTab === 'chart') return;
    setDetailsError('');
    // Seed from bins list is already shown; refresh details silently in background
    binService.getBinById(selectedBinId, { detailsOnly: true })
      .then(data => {
        setSelectedBin(prev => prev
          ? { ...prev, details: data.details, snapshot: data.snapshot || prev.snapshot, latest_telemetry: data.latest_telemetry || prev.latest_telemetry }
          : data);
      })
      .catch(() => {}) // silent fail — seed data already visible
      .finally(() => setDetailsLoading(false));
  }, [selectedBinId, innerTab]);

  // Load chart telemetry when chart view is active or range/metric changes (with in-session cache)
  useEffect(() => {
    if (!selectedBinId || innerTab !== 'chart' || !chartMetric) {
      if (innerTab === 'chart') setDetailsLoading(false);
      return;
    }
    const cacheKey = `${selectedBinId}:${chartRange}:${chartMetric}`;
    const cached = telemetryCache.current.get(cacheKey);
    if (cached) {
      setSelectedBin(prev => prev ? { ...prev, telemetry: cached } : { telemetry: cached });
      setDetailsLoading(false);
      return;
    }
    setDetailsLoading(true);
    setDetailsError('');
    binService.getBinTelemetry(selectedBinId, { range: chartRange, metric: chartMetric })
      .then(data => {
        const telem = data?.telemetry || [];
        telemetryCache.current.set(cacheKey, telem);
        setSelectedBin(prev => prev ? { ...prev, telemetry: telem } : { telemetry: telem });
      })
      .catch(err => { setDetailsError(err.response?.data?.msg || err.message || 'Error loading bin'); })
      .finally(() => setDetailsLoading(false));
  }, [selectedBinId, innerTab, chartRange, chartMetric]);

  // Soft delete helpers
  const handleDeleteBin = (e, bin) => { e.stopPropagation(); setConfirmState({ open:true, mode:'delete', bin, loading:false }); };
  const handleRestoreBin = (e, bin) => { e.stopPropagation(); setConfirmState({ open:true, mode:'restore', bin, loading:false }); };
  const beginEditBin = (e, bin) => {
    e.stopPropagation();
    if(bin.is_deleted) return;
    setEditingBinId(bin.bin_id);
    setEditError('');
    setEditValues({
      bin_identifier: bin.bin_identifier || '',
      site_id: bin.site_id || '',
      latitude: bin.location?.coordinates ? String(bin.location.coordinates[1]) : '',
      longitude: bin.location?.coordinates ? String(bin.location.coordinates[0]) : '',
      address: bin.address || ''
    });
  };
  const cancelEditBin = (e) => { e.stopPropagation(); setEditingBinId(null); setEditError(''); setEditSuggestions([]); };
  const onEditFieldChange = (field,val) => { setEditValues(v => ({ ...v, [field]: val })); };
  // Watch address changes while editing for live geocode (always allow suggestions now, even if coords filled)
  useEffect(() => {
    if(!editingBinId) { setLiveGeo(null); setEditSuggestions([]); return; }
    if(!editValues.address || editValues.address.trim().length < 3){ setLiveGeo(null); setEditSuggestions([]); return; }
    let cancelled = false;
    setGeoStatus('loading');
    debouncedGeocodeMulti(editValues.address.trim()).then(res => {
      if(cancelled) return;
      if(suppressBinEditSuggestions) return; // ignore late async results after selection
      if(Array.isArray(res) && res.length){
        setEditSuggestions(res);
        setLiveGeo({ lat: res[0].latitude, lon: res[0].longitude });
        setGeoStatus('ok');
        setGeoVersion(v=>v+1);
      } else { setEditSuggestions([]); setLiveGeo(null); setGeoStatus('fail'); }
    });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingBinId, editValues.address, suppressBinEditSuggestions]);

  // Recompute portal position when suggestions change / window resizes / scrolls
  useEffect(() => {
    if(!editingBinId || editSuggestions.length === 0 || suppressBinEditSuggestions) { setSuggestPos(null); return; }
    const el = addressInputRef.current;
    if(!el) { setSuggestPos(null); return; }
    const rect = el.getBoundingClientRect();
    setSuggestPos({ top: rect.bottom + window.scrollY + 2, left: rect.left + window.scrollX, width: Math.max(rect.width, 260) });
    const handler = () => {
      const r = el.getBoundingClientRect();
      setSuggestPos({ top: r.bottom + window.scrollY + 2, left: r.left + window.scrollX, width: Math.max(r.width, 260) });
    };
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => { window.removeEventListener('scroll', handler, true); window.removeEventListener('resize', handler); };
  }, [editingBinId, editSuggestions, suppressBinEditSuggestions]);

  // Keyboard navigation inside suggestions
  useEffect(() => {
    if(!suggestPos) { setHighlightIdx(-1); return; }
    const onKey = (e) => {
      if(!editSuggestions.length) return;
      if(['ArrowDown','ArrowUp','Enter','Escape','Esc'].includes(e.key)){
        if(e.key === 'ArrowDown') { e.preventDefault(); setHighlightIdx(i => (i+1) % editSuggestions.length); }
        else if(e.key === 'ArrowUp') { e.preventDefault(); setHighlightIdx(i => (i-1+editSuggestions.length) % editSuggestions.length); }
        else if(e.key === 'Escape' || e.key === 'Esc') { e.preventDefault(); setEditSuggestions([]); setSuggestPos(null); }
        else if(e.key === 'Enter') {
          if(highlightIdx >=0 && highlightIdx < editSuggestions.length){
            const sug = editSuggestions[highlightIdx];
            const formatted = sug.formatted || sug.display || '';
            onEditFieldChange('address', formatted);
            if(sug.latitude && sug.longitude){
              onEditFieldChange('latitude', String(sug.latitude));
              onEditFieldChange('longitude', String(sug.longitude));
            }
            setSuppressBinEditSuggestions(true);
            setTimeout(()=> setSuppressBinEditSuggestions(false), 400);
            setEditSuggestions([]);
            setSuggestPos(null);
          }
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [suggestPos, editSuggestions, highlightIdx]);

  // Close suggestion list if clicking elsewhere
  useEffect(() => {
    if(!suggestPos) return;
    const handler = (e) => {
      if(addressInputRef.current && addressInputRef.current.contains(e.target)) return;
      // Click outside portal container (handled separately there) will close
      setEditSuggestions([]); setSuggestPos(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [suggestPos]);
  const saveEditBin = async (e, bin) => {
    e.stopPropagation();
    if(!editingBinId) return;
    setEditError('');
    const payload = { bin_identifier: editValues.bin_identifier.trim(), site_id: parseInt(editValues.site_id,10) };
    if(editValues.address.trim()) payload.address = editValues.address.trim();
    const latNum = parseFloat(editValues.latitude.replace(',','.'));
    const lonNum = parseFloat(editValues.longitude.replace(',','.'));
    if(!isNaN(latNum) && !isNaN(lonNum)) { payload.latitude = latNum; payload.longitude = lonNum; }
    try {
      const updated = await binService.updateBin(bin.bin_id, payload);
      // Optimistic immediate update (address & coords) so user sees change azonnal
      setBins(prev => prev.map(b => {
        if(b.bin_id !== bin.bin_id) return b;
        return {
          ...b,
          bin_identifier: updated.bin_identifier ?? payload.bin_identifier,
            site_id: updated.site_id ?? payload.site_id,
            location: updated.location ?? b.location,
            address: updated.address ?? payload.address ?? b.address
        };
      }));
      // Ha a szűrő másik telephelyre állítja át a kukát és emiatt eltűnne, jelezzünk.
      if(filterSiteId && String(filterSiteId) !== String(payload.site_id)) {
        setInfoMessage(t('bins.siteChangedHidden') || 'A kuka másik telephelyre került és a jelenlegi szűrés miatt eltűnt.');
        setTimeout(()=> setInfoMessage(''), 6000);
      }
      // Ha a részletek nézetben ugyanaz a bin van kiválasztva, frissítjük ott is
      setSelectedBin(prev => {
        if(!prev || !prev.details || prev.details.bin_id !== bin.bin_id) return prev;
        return {
          ...prev,
          details: {
            ...prev.details,
            bin_identifier: updated.bin_identifier ?? payload.bin_identifier,
            location: updated.location ?? prev.details.location,
            address: updated.address ?? payload.address ?? prev.details.address
          }
        };
      });
      setEditingBinId(null);
    } catch(err){
      const apiMsg = err.response?.data?.msg || err.message || 'Save failed';
      setEditError(apiMsg);
    }
  };

  // Details tab: drag & drop position editing (koordináták szerkesztése a mini térképen)
  const beginPositionEdit = () => {
    const coords = selectedBin?.details?.location?.coordinates;
    if (!coords) return;
    setDraftPosition({ lat: coords[1], lng: coords[0] });
    setPositionEditMode(true);
    setPositionError('');
  };

  const cancelPositionEdit = () => {
    setPositionEditMode(false);
    setDraftPosition(null);
    setPositionError('');
  };

  const savePositionEdit = async () => {
    const det = selectedBin?.details;
    if (!det || !draftPosition) return;
    setSavingPosition(true);
    setPositionError('');
    try {
      const payload = {
        bin_identifier: det.bin_identifier,
        site_id: det.site_id,
        latitude: draftPosition.lat,
        longitude: draftPosition.lng
      };
      if (det.address) payload.address = det.address;
      const updated = await binService.updateBin(det.bin_id, payload);
      const newLocation = updated.location ?? { type: 'Point', coordinates: [draftPosition.lng, draftPosition.lat] };
      setBins(prev => prev.map(b => b.bin_id === det.bin_id ? { ...b, location: newLocation } : b));
      setSelectedBin(prev => prev ? { ...prev, details: { ...prev.details, location: newLocation } } : prev);
      setPositionEditMode(false);
      setDraftPosition(null);
    } catch (err) {
      const apiMsg = err.response?.data?.msg || err.message || 'Save failed';
      setPositionError(apiMsg);
    } finally {
      setSavingPosition(false);
    }
  };

  const performBinConfirm = () => {
    if(!confirmState.bin || !confirmState.mode) return;
    setConfirmState(cs => ({ ...cs, loading:true }));
    const id = confirmState.bin.bin_id;
    const p = confirmState.mode === 'delete' ? binService.deleteBin(id) : binService.restoreBin(id);
    p.then(() => {
      setBins(prev => prev.map(b => b.bin_id === id ? { ...b, is_deleted: confirmState.mode === 'delete' } : b));
      if(confirmState.mode === 'delete' && selectedBinId === id) setSelectedBinId(null);
      setConfirmState({ open:false, mode:null, bin:null, loading:false });
    }).catch(err => {
      console.error('Bin confirm action failed', err);
      setConfirmState({ open:false, mode:null, bin:null, loading:false });
    });
  };
  return (
  <div className="sb-page" style={{ height: '100%', minHeight: 'calc(100vh - 100px)' }}>
      <div className="tab-buttons" style={{ display:'flex', alignItems:'center', gap:8 }}>
        <button
          type="button"
          onClick={() => setActiveTab('map')}
          className={`btn tab-button ${activeTab === 'map' ? 'active' : ''}`}
        >{t('bins.map')}</button>
        <button
          type="button"
          onClick={() => setActiveTab('list')}
          className={`btn tab-button ${activeTab === 'list' ? 'active' : ''}`}
        >{t('bins.list')}</button>
        <button
          type="button"
          onClick={() => setActiveTab('details')}
          disabled={!selectedBinId}
          className={`btn tab-button ${activeTab === 'details' ? 'active' : ''}`}
        >{t('bins.detailsTab') || 'Details'}</button>
        {SHOW_RT_STATUS && (
          <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:6, fontSize:11, padding:'0 6px' }}>
            <span style={{ display:'inline-flex', alignItems:'center', gap:4 }}>
              <span style={{ width:10, height:10, borderRadius:'50%', background: rtStatus==='open' ? '#2e7d32' : (rtStatus==='connecting' ? '#f9a825' : '#c62828'), boxShadow:'0 0 0 1px rgba(0,0,0,.15)' }} />
              {rtStatus==='open' ? 'LIVE' : rtStatus==='connecting' ? 'Kapcsolódás…' : 'Szétkapcsolva'}
            </span>
            {lastLatency!=null && rtStatus==='open' && <span style={{ opacity:.55 }}>~{lastLatency}ms</span>}
          </div>
        )}
      </div>
  {activeTab === 'map' && (
    <MapView
      bins={bins}
      sites={sites}
      mapFillFilters={mapFillFilters}
      mapShowAll={mapShowAll}
      setMapFillFilters={setMapFillFilters}
      setMapShowAll={setMapShowAll}
      filterSiteId={filterSiteId}
      onSelect={(id)=> { selectBinById(id); }}
    />
  )}
      {activeTab === 'list' && (
        <div style={{ marginTop: 8 }}>
          {infoMessage && (
            <div style={{ background:'#fff8e1', border:'1px solid #ffe082', padding:'6px 10px', borderRadius:4, fontSize:12, marginBottom:8, color:'#795548' }}>
              {infoMessage}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <h3 style={{ margin: '4px 0' }}>{t('bins.listTitle')}</h3>
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              <button type="button" className="btn" onClick={() => fetchBins()} style={{ padding:'4px 10px' }}>{t('common.refresh') || 'Refresh'}</button>
            </div>
          </div>
          {canCreate && (
            <form onSubmit={handleCreateBin} style={{ margin: '12px 0 16px', background: '#fafafa', border: '1px solid #e0e0e0', padding: '10px 12px', borderRadius: 6, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 160 }}>
                <label style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{t('bins.form.identifier')}</label>
                <input className="form-control" value={newIdentifier} onChange={e=> setNewIdentifier(e.target.value)} placeholder={t('bins.form.identifierPlaceholder')} disabled={creating} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 220, flex: '1 1 240px', position:'relative' }}>
                <label style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{t('sites.fields.address')}</label>
                <input className="form-control" value={newAddress} onChange={e=> {
                  const v = e.target.value; setNewAddress(v);
                  if (suppressBinCreateSuggestions) setSuppressBinCreateSuggestions(false);
                  if(!v.trim() || v.trim().length < 3){ setCreateSuggestions([]); setCreateGeoStatus('idle'); return; }
                  // Keep suggesting even if lat/lon already filled to allow correction
                  setCreateGeoStatus('loading');
                  // Smart geocode: notable először, ha nincs vagy utca+szám minta -> teljes találat lista
                  debouncedGeocodeMultiSmart(v.trim()).then(res => {
                    if (suppressBinCreateSuggestions) return; // ignore late response after selection
                    if(Array.isArray(res)) { setCreateSuggestions(res); setCreateGeoStatus(res.length ? 'ok' : 'fail'); }
                    else { setCreateSuggestions([]); setCreateGeoStatus('fail'); }
                  });
                }} placeholder={t('sites.placeholders.address')} disabled={creating || geocoding} />
                {createGeoStatus === 'loading' && <div className="geo-spinner-wrapper"><div className="geo-spinner" /></div>}
                {/* Removed loading/fail inline text to avoid height jump */}
                {createSuggestions.length > 0 && (
                  <div style={{ position:'absolute', top:'100%', left:0, right:0, background:'#fff', border:'1px solid #ccc', zIndex:30, maxHeight:180, overflowY:'auto', borderRadius:4, boxShadow:'0 2px 4px rgba(0,0,0,0.15)' }}>
                    {createSuggestions.map((sug,i)=> {
                      const comps = sug.components || {};
                      const formatted = [comps.postcode, comps.city, comps.street, comps.number].filter(Boolean).join(' ');
                      return (
                        <div
                          key={i}
                          style={{ padding:'4px 8px', fontSize:11, cursor:'pointer', lineHeight:1.3 }}
                          onClick={()=>{
                            setNewLat(String(sug.latitude));
                            setNewLng(String(sug.longitude));
                            setNewAddress(formatted || sug.display || newAddress);
                            setSuppressBinCreateSuggestions(true);
                            setCreateSuggestions([]);
                          }}
                        >
                          <strong>{sug.latitude.toFixed(5)}, {sug.longitude.toFixed(5)}</strong>
                          {sug._notable && <span title={t('geocode.notableHint') || 'Nevezetes találat'} className="geo-notable-dot" />}
                          <br/>
                          <span style={{ opacity:.8 }}>{sug.display}</span>
                          {formatted && <div style={{ fontSize:9, opacity:.55, marginTop:2 }}>{formatted}</div>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 180 }}>
                <label style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{t('sites.fields.site')}</label>
                <select className="form-control" value={selectedSiteId} onChange={e=> setSelectedSiteId(e.target.value)} disabled={creating || siteLoading}>
                  <option value="">{siteLoading ? (t('common.loading') || '...') : t('sites.selectPlaceholder')}</option>
                  {sites.map(s => <option key={s.site_id} value={s.site_id}>{s.site_code} - {s.site_name}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', width: 120 }}>
                <label style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{t('bins.details.latitude')}</label>
                <input className="form-control" value={newLat} onChange={e=> setNewLat(e.target.value)} placeholder={t('bins.form.latitudePlaceholder')} disabled={creating} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', width: 120 }}>
                <label style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{t('bins.details.longitude')}</label>
                <input className="form-control" value={newLng} onChange={e=> setNewLng(e.target.value)} placeholder={t('bins.form.longitudePlaceholder')} disabled={creating} />
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button type="submit" className="btn btn-primary" disabled={creating} style={{ padding: '6px 14px' }}>{creating ? t('bins.form.saving') : t('bins.form.add')}</button>
                {createError && <span style={{ color: '#c62828', fontSize: 12 }}>{createError}</span>}
              </div>
            </form>
          )}
          {/* Toolbar: refresh + hide deleted + site filter */}
          <div style={{ display:'flex', alignItems:'center', gap:16, margin:'6px 0 10px', flexWrap:'wrap' }}>
            <label style={{ fontSize:12, display:'flex', alignItems:'center', gap:6 }}>
              <input type="checkbox" checked={hideDeleted} onChange={e=> setHideDeleted(e.target.checked)} /> {t('common.hideDeleted') || 'Töröltek elrejtése'}
            </label>
            <div style={{ display:'flex', alignItems:'center', gap:6 }}>
              <span style={{ fontSize:12 }}>{t('sites.fields.site') || 'Telephely'}:</span>
              <select
                className="form-control"
                style={{ fontSize:12, padding:'2px 6px', minWidth:140 }}
                value={filterSiteId}
                onChange={e=> {
                  const v = e.target.value; setFilterSiteId(v);
                  // Ha épp egy kiválasztott bin eltűnne a szűrés miatt, reseteljük a details nézetet
                  setSelectedBinId(prev => {
                    if(!prev) return prev;
                    if(v === '') return prev; // all
                    const b = bins.find(x => x.bin_id === prev);
                    if(!b) return null;
                    if(String(b.site_id) !== v) return null;
                    return prev;
                  });
                  // Reset ordering mode when changing filter
                  setOrderingMode(false); setPendingOrder([]); setOrderError('');
                }}
              >
                <option value="">{t('common.all') || 'Összes'}</option>
                {sites.map(s => <option key={s.site_id} value={s.site_id}>{s.site_code} - {s.site_name}</option>)}
              </select>
            </div>
            <div style={{ fontSize:11, opacity:.65 }}>
              {/* Summary counter */}
              {(() => {
                const total = bins.length;
                const visible = bins.filter(b => {
                  if(hideDeleted && b.is_deleted) return false;
                  if(filterSiteId && String(b.site_id) !== filterSiteId) return false;
                  return true;
                }).length;
                return visible + ' / ' + total;
              })()}
            </div>
            {/* Ordering mode toggle – always visible for admin, disabled without site filter */}
            {userRole === 'admin' && (
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <button
                  type="button"
                  className="btn btn-sm"
                  disabled={!filterSiteId}
                  title={!filterSiteId ? (t('bins.selectSiteFirst') || 'Válassz telephelyet') : undefined}
                  style={{ background: orderingMode ? '#6d4c41' : '#455a64', color:'#fff', fontSize:11, padding:'4px 10px', border:'none', borderRadius:4, opacity: !filterSiteId ? 0.5 : 1 }}
                  onClick={()=> { if(orderingMode){ setOrderingMode(false); setPendingOrder([]); } else { setOrderingMode(true); setPendingOrder([]); } setOrderError(''); }}
                >{orderingMode ? (t('common.orderingStop') || 'End order') : (t('common.orderingStart') || 'Set order')}</button>
                {orderingMode && (
                  <>
                    <button
                      type="button"
                      className="btn btn-sm"
                      disabled={savingOrder || pendingOrder.length===0}
                      style={{ background:'#2e7d32', color:'#fff', fontSize:11, padding:'4px 10px', border:'none', borderRadius:4 }}
                      onClick={async ()=> {
                        if(!filterSiteId) return; setSavingOrder(true); setOrderError('');
                        try {
                          await binService.updateOrder(parseInt(filterSiteId,10), pendingOrder);
                          await fetchBins(true);
                          // Auto-exit ordering mode after successful save
                          setPendingOrder([]);
                          setOrderingMode(false);
                        } catch(err){
                          const apiMsg = err.response?.data?.msg || err.message;
                          setOrderError(apiMsg);
                        } finally { setSavingOrder(false); }
                      }}
                    >{savingOrder ? (t('common.saving') || 'Saving...') : (t('common.orderingSave') || 'Save order')}</button>
                    <button
                      type="button"
                      className="btn btn-sm"
                      disabled={savingOrder}
                      style={{ background:'#757575', color:'#fff', fontSize:11, padding:'4px 10px', border:'none', borderRadius:4 }}
                      onClick={()=> { setPendingOrder([]); setOrderingMode(false); setOrderError(''); }}
                    >{t('common.orderingCancel') || 'Cancel'}</button>
                  </>
                )}
                {orderingMode && <span style={{ fontSize:11, opacity:.7 }}>{t('common.orderingMode') || 'Ordering mode'}: {pendingOrder.length}</span>}
              </div>
            )}
            {/* Collection list (print) button – always visible, disabled without site filter */}
            <button
              type="button"
              className="btn btn-sm"
              disabled={!filterSiteId}
              title={!filterSiteId ? (t('bins.selectSiteFirst') || 'Válassz telephelyet') : undefined}
              style={{ background:'#0277bd', color:'#fff', fontSize:11, padding:'4px 10px', border:'none', borderRadius:4, opacity: !filterSiteId ? 0.5 : 1 }}
              onClick={()=> setCollectionModal(m => ({ ...m, open:true, siteId: filterSiteId }))}
            >{t('bins.collectionList') || 'Ürítési lista'}</button>
            {/* Maintenance list (low battery) button – always visible, disabled without site filter */}
            <button
              type="button"
              className="btn btn-sm"
              disabled={!filterSiteId}
              title={!filterSiteId ? (t('bins.selectSiteFirst') || 'Válassz telephelyet') : undefined}
              style={{ background:'#6a1b9a', color:'#fff', fontSize:11, padding:'4px 10px', border:'none', borderRadius:4, opacity: !filterSiteId ? 0.5 : 1 }}
              onClick={()=> {
                const th = getCachedThresholds();
                const defV = Number(th.battery_low_threshold_v || 11);
                setMaintenanceModal(m => ({ ...m, open:true, siteId: filterSiteId, minV: defV }));
              }}
            >{t('bins.maintenanceList') || 'Karbantartási lista'}</button>
            {orderError && <div style={{ fontSize:11, color:'#c62828' }}>{orderError}</div>}
          </div>
          {(bins.length > 0) ? (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
               <thead>
                 <tr style={{ textAlign: 'left', background: '#f5f5f5' }}>
                  {/* Ordering column FIRST when site filter active */}
                  {filterSiteId && <th style={{ padding: '8px 10px', borderBottom: '1px solid #ddd', width:70 }}>{t('common.orderingCol') || 'Sorrend'}</th>}
                  <th style={{ padding: '8px 10px', borderBottom: '1px solid #ddd' }}>ID</th>
                  <th style={{ padding: '8px 10px', borderBottom: '1px solid #ddd', minWidth:140 }}>{t('sites.fields.site') || 'Telephely'}</th>
                  {/* Removed separate Name column: bin_identifier already displayed in ID column */}
                  {/* Address & Coordinates split */}
                  <th style={{ padding: '8px 10px', borderBottom: '1px solid #ddd', minWidth:180 }}>{t('common.address') || 'Cím'}</th>
                  <th style={{ padding: '8px 10px', borderBottom: '1px solid #ddd', minWidth:150 }}>{t('common.coordinates') || 'Koordináták'}</th>
                  <th
                    style={{ padding: '8px 10px', borderBottom: '1px solid #ddd', cursor:'pointer' }}
                    onClick={() => {
                      setSortField(f => f === 'state' ? 'state' : 'state');
                      setSortDir(d => (sortField === 'state' ? (d === 'asc' ? 'desc' : 'asc') : 'asc'));
                      setSortField('state');
                    }}
                  >{t('bins.details.stats.state')}{sortField==='state' && <span style={{ marginLeft:4 }}>{sortDir==='asc'?'▲':'▼'}</span>}</th>
                  <th
                    style={{ padding: '8px 10px', borderBottom: '1px solid #ddd', cursor:'pointer' }}
                    onClick={() => {
                      setSortDir(d => (sortField === 'fill' ? (d === 'asc' ? 'desc' : 'asc') : 'asc'));
                      setSortField('fill');
                    }}
                  >{t('bins.details.stats.filling')}{sortField==='fill' && <span style={{ marginLeft:4 }}>{sortDir==='asc'?'▲':'▼'}</span>}</th>
                  <th
                    style={{ padding: '8px 10px', borderBottom: '1px solid #ddd', cursor:'pointer' }}
                    onClick={() => {
                      setSortDir(d => (sortField === 'battery' ? (d === 'asc' ? 'desc' : 'asc') : 'asc'));
                      setSortField('battery');
                    }}
                  >{t('bins.details.stats.battery')}{sortField==='battery' && <span style={{ marginLeft:4 }}>{sortDir==='asc'?'▲':'▼'}</span>}</th>
                  <th style={{ padding: '8px 10px', borderBottom: '1px solid #ddd' }}>{t('bins.details.stats.signal')}</th>
                  { (userRole === 'admin') && <th style={{ padding: '8px 10px', borderBottom: '1px solid #ddd', minWidth:180 }}>{t('users.actions') || 'Actions'}</th> }
                 </tr>
               </thead>
              <tbody>
                {bins.filter(b => {
                  if(hideDeleted && b.is_deleted) return false;
                  if(filterSiteId && String(b.site_id) !== filterSiteId) return false;
                  return true;
                }).slice().sort((a,b) => {
                  if(!sortField) return 0;
                  const dir = sortDir === 'asc' ? 1 : -1;
                  if(sortField==='state'){
                    const aOpen = (() => { if(a.latest_telemetry?.latch_state != null) return a.latest_telemetry.latch_state === 0; const ds=a.latest_telemetry?.door_status||a.snapshot?.door_status; if(ds){ const main=ds.main||ds.state||''; const latch=ds.latch||''; return /open|unlocked/i.test(main) || /open|unlocked/i.test(latch);} return false; })();
                    const bOpen = (() => { if(b.latest_telemetry?.latch_state != null) return b.latest_telemetry.latch_state === 0; const ds=b.latest_telemetry?.door_status||b.snapshot?.door_status; if(ds){ const main=ds.main||ds.state||''; const latch=ds.latch||''; return /open|unlocked/i.test(main) || /open|unlocked/i.test(latch);} return false; })();
                    return (aOpen===bOpen?0:(aOpen?1:-1))*dir;
                  }
                  if(sortField==='fill'){
                    const av = typeof a.latest_telemetry?.fill_level==='number'?a.latest_telemetry.fill_level:-1;
                    const bv = typeof b.latest_telemetry?.fill_level==='number'?b.latest_telemetry.fill_level:-1;
                    return (av===bv?0:(av>bv?1:-1))*dir;
                  }
                  if(sortField==='battery'){
                    const av = typeof a.latest_telemetry?.battery_level==='number'?a.latest_telemetry.battery_level:-1;
                    const bv = typeof b.latest_telemetry?.battery_level==='number'?b.latest_telemetry.battery_level:-1;
                    return (av===bv?0:(av>bv?1:-1))*dir;
                  }
                  return 0;
                }).map(b => {
                  const fill = b.latest_telemetry?.fill_level;
                  const battery = b.latest_telemetry?.battery_level;
                  const signalRssi = b.latest_telemetry?.rssi ?? b.snapshot?.rssi ?? b.latest_telemetry?.signal?.rssi ?? b.snapshot?.signal?.rssi;
                  const signalObj = buildSignalFromRssi(signalRssi); // { quality, rssi, csq }
                  const signalLabel = (() => {
                    const q = signalObj?.quality;
                    if (q === 'excellent') return t('bins.details.stats.signalExcellent') || 'Kiváló';
                    if (q === 'good') return t('bins.details.stats.signalGood') || 'Jó';
                    if (q === 'weak') return t('bins.details.stats.signalWeak') || 'Gyenge';
                    return null;
                  })();
                  const deleted = b.is_deleted;
                  const isEditing = editingBinId === b.bin_id;
                  const hasAddress = !!(b.address && String(b.address).trim());
                  const addressDisplay = hasAddress ? String(b.address).trim() : '';
                  const coordsDisplay = (() => {
                    if(b.location && b.location.coordinates){
                      const lat = b.location.coordinates[1];
                      const lon = b.location.coordinates[0];
                      if(typeof lat === 'number' && typeof lon === 'number') return lat.toFixed(5) + ', ' + lon.toFixed(5);
                    }
                    return '';
                  })();
                  const orderVal = b.display_order != null ? b.display_order : '';
                  return (
                    <tr
                      key={b.bin_id}
                      style={{ cursor: 'pointer', opacity: deleted ? 0.5 : 1, background: deleted ? '#fafafa' : (orderingMode && pendingOrder.includes(b.bin_id) ? '#e3f2fd' : undefined) }}
                      onClick={() => {
                        if(orderingMode){
                          if(deleted) return;
                          if(filterSiteId && String(b.site_id) === filterSiteId){
                            setPendingOrder(prev => prev.includes(b.bin_id) ? prev : [...prev, b.bin_id]);
                          }
                          return;
                        }
                        if(!deleted) { selectBin(b.bin_id, buildSelectedBinSeed(b)); }
                      }}
                    >
                      {/* Ordering value first */}
                      {filterSiteId && (
                        <td style={{ padding: '6px 10px', borderBottom: '1px solid #eee', textAlign:'center', fontSize:12 }}>
                          {orderingMode ? (
                            pendingOrder.includes(b.bin_id) ? (pendingOrder.indexOf(b.bin_id)+1) : ''
                          ) : orderVal}
                        </td>
                      )}
                      <td style={{ padding: '6px 10px', borderBottom: '1px solid #eee', fontFamily: 'monospace' }}>
                        {isEditing ? (
                          <input
                            style={{ width: '100%', fontFamily: 'monospace' }}
                            value={editValues.bin_identifier}
                            onChange={e=> onEditFieldChange('bin_identifier', e.target.value)}
                            onClick={ev=> ev.stopPropagation()}
                          />
                        ) : b.bin_identifier}
                      </td>
                      {/* Site column */}
                      <td style={{ padding: '6px 10px', borderBottom:'1px solid #eee', fontSize:12 }}>
                        {isEditing ? (
                          <select
                            className="form-control"
                            value={editValues.site_id}
                            onClick={ev=> ev.stopPropagation()}
                            onChange={e=> onEditFieldChange('site_id', e.target.value)}
                            style={{ fontSize:12, padding:'2px 4px' }}
                          >
                            <option value="">{t('sites.selectPlaceholder') || 'Válassz...'}</option>
                            {sites.map(s => (
                              <option key={s.site_id} value={s.site_id}>{s.site_code} - {s.site_name}</option>
                            ))}
                          </select>
                        ) : (() => {
                          const site = sites.find(s => String(s.site_id) === String(b.site_id));
                          return site ? (site.site_code + (site.site_name ? (' - ' + site.site_name) : '')) : (b.site_id || '—');
                        })()}
                      </td>
                      {/* Removed former Name column cell */}
                      {/* Address column content */}
                      <td style={{ padding: '6px 10px', borderBottom: '1px solid #eee', fontSize:12, opacity:.85 }}>
                        {isEditing ? (
                          <div style={{ display:'flex', flexDirection:'column', gap:4 }}
                               onClick={ev=> ev.stopPropagation()}
                               onMouseDown={ev=> ev.stopPropagation()}>
                            <input
                              className="form-control"
                              style={{ fontSize:12 }}
                              value={editValues.address}
                              placeholder={t('sites.placeholders.address')}
                              ref={addressInputRef}
                              onChange={e=> { setHighlightIdx(-1); onEditFieldChange('address', e.target.value); }}
                            />
                          </div>
                        ) : (hasAddress ? addressDisplay : <span style={{ textDecoration:'line-through', opacity:.6 }}>—</span>)}
                      </td>
                      {/* Coordinates column content */}
                      <td style={{ padding: '6px 10px', borderBottom: '1px solid #eee', fontSize:12, fontFamily:'monospace', opacity:.85 }}>
                        {isEditing ? (
                          <div style={{ display:'flex', gap:4 }} onClick={ev=> ev.stopPropagation()}>
                            <input
                              className="form-control"
                              style={{ width: '50%', fontSize:12 }}
                              placeholder="lat"
                              value={editValues.latitude}
                              onChange={e=> onEditFieldChange('latitude', e.target.value)}
                            />
                            <input
                              className="form-control"
                              style={{ width: '50%', fontSize:12 }}
                              placeholder="lng"
                              value={editValues.longitude}
                              onChange={e=> onEditFieldChange('longitude', e.target.value)}
                            />
                          </div>
                        ) : (coordsDisplay || '—')}
                      </td>
                      {(() => {
                        const doorStatus = b.latest_telemetry?.door_status || b.snapshot?.door_status;
                        const latchOpen = (() => {
                          if (b.latest_telemetry?.latch_state != null) return b.latest_telemetry.latch_state === 0;
                          const ds = doorStatus;
                          if (ds) {
                            const mainState = ds.main || ds.state || '';
                            const latchField = ds.latch || '';
                            return /open/i.test(mainState) || /unlocked|open/i.test(latchField);
                          }
                          return false;
                        })();
                        // Display only Nyitva / Zárva (translated) instead of raw summary
                        const label = latchOpen ? (t('bins.latch.open') || 'Nyitva') : (t('bins.latch.closed') || 'Zárva');
                        return (
                          <td style={{ padding: '6px 10px', borderBottom: '1px solid #eee' }}>
                            <span style={{ color: latchOpen ? '#2e7d32' : '#c62828', fontWeight:600 }}>{label}</span>
                          </td>
                        );
                      })()}
                      <td style={{ padding: '6px 10px', borderBottom: '1px solid #eee', minWidth:110 }}>
                        {(() => {
                          if(fill == null || isNaN(fill)){
                            return <div className="fill-bar-wrapper fb-grey" title={t('bins.details.stats.filling') + ': —'}>
                              <div className="fill-bar-track" />
                              <div className="fill-bar-fill grey" style={{ width:'100%', opacity:.25 }} />
                              <div className="fill-bar-label">—</div>
                            </div>;
                          }
                          const pctRaw = Math.max(0, Math.min(100, fill));
                          const pct = Math.round(pctRaw);
                          let colorClass = 'green';
                          const th = getCachedThresholds();
                          const g = typeof th.fill_green_threshold_pct === 'number' ? th.fill_green_threshold_pct : 70;
                          const r = typeof th.fill_red_threshold_pct === 'number' ? th.fill_red_threshold_pct : 90;
                          // Yellow is implicit: between green upper (g) and red lower (r)
                          if(pct >= r) colorClass = 'red';
                          else if(pct >= g) colorClass = 'yellow';
                          let wrapperTone = 'fb-' + (colorClass === 'green' ? 'green' : colorClass === 'yellow' ? 'yellow' : colorClass === 'red' ? 'red' : 'grey');
                          if(colorClass === 'green' && pct < 50) wrapperTone += ' low';
                          return <div className={"fill-bar-wrapper " + wrapperTone} title={t('bins.details.stats.filling') + ': ' + pct + '%'}>
                            <div className="fill-bar-track" />
                            <div className={"fill-bar-fill " + colorClass} style={{ width: pct + '%' }} />
                            <div className="fill-bar-label">{pct}%</div>
                          </div>;
                        })()}
                      </td>
                      <td style={{ padding: '6px 10px', borderBottom: '1px solid #eee' }}>{(() => { if (battery == null) return '—'; const mv = Number(battery); if (isNaN(mv)) return '—'; if (mv > 16800) return 'Hibás adat'; const v=(mv/1000); const val=v.toFixed(1)+' V'; const lowV = Number(getCachedThresholds().battery_low_threshold_v || 11); const low=v<lowV; return (
                        <span>
                          {val}
                          {low && <span style={{ color:'#c62828', fontSize:11, marginLeft:6 }}>Gyenge</span>}
                        </span>
                      ); })()}</td>
                      <td style={{ padding: '6px 10px', borderBottom: '1px solid #eee' }}>
                            {(() => {
                              const q = signalObj?.quality || 'unknown';
                              if (!signalObj || !q) return '—';
                              const label = signalLabel || '';
                              const parts = [];
                              if (label) parts.push(label);
                              if (signalObj.rssi != null) parts.push(`RSSI: ${signalObj.rssi} dBm`);
                              if (signalObj.csq != null) parts.push(`CSQ: ${signalObj.csq}`);
                              const title = parts.length ? parts.join(' \u2022 ') : undefined;
                              return (
                                <div style={{ display:'flex', alignItems:'center', gap:6 }} title={title}>
                                  <SignalIcon quality={q} size={16} />
                                  <span style={{ fontSize:12, opacity:.8 }}>{label}</span>
                                </div>
                              );
                            })()}
                          </td>
                      { (userRole === 'admin') && (
                        <td style={{ padding: '6px 8px', borderBottom: '1px solid #eee' }} onClick={(ev)=> ev.stopPropagation()}>
                          {!deleted && !isEditing && (
                            <>
                              <button type="button" className="btn btn-sm" style={{ background:'#1565c0', color:'#fff', fontSize:11, padding:'3px 8px', border:'none', borderRadius:4, marginRight:4 }} onClick={(ev)=> beginEditBin(ev,b)}>{t('users.edit') || t('common.edit') || 'Edit'}</button>
                              <button type="button" className="btn btn-sm" style={{ background:'#e53935', color:'#fff', fontSize:11, padding:'3px 8px', border:'none', borderRadius:4 }} onClick={(ev)=> handleDeleteBin(ev,b)}>{t('bins.delete') || 'Delete'}</button>
                            </>
                          )}
                          {!deleted && isEditing && (
                            <>
                              <button type="button" className="btn btn-sm" style={{ background:'#2e7d32', color:'#fff', fontSize:11, padding:'3px 8px', border:'none', borderRadius:4, marginRight:4 }} onClick={(ev)=> saveEditBin(ev,b)}>{t('users.save') || t('common.save') || 'Save'}</button>
                              <button type="button" className="btn btn-sm" style={{ background:'#757575', color:'#fff', fontSize:11, padding:'3px 8px', border:'none', borderRadius:4 }} onClick={cancelEditBin}>{t('users.cancel') || t('common.cancel') || 'Cancel'}</button>
                            </>
                          )}
                          {deleted && <button type="button" className="btn btn-sm" style={{ background:'#2e7d32', color:'#fff', fontSize:11, padding:'3px 8px', border:'none', borderRadius:4 }} onClick={(ev)=> handleRestoreBin(ev,b)}>{t('bins.restore') || 'Restore'}</button>}
                          {isEditing && editError && <div style={{ fontSize:10, color:'#c62828', marginTop:4 }}>{editError}</div>}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <p style={{ fontSize: 13, opacity: .75 }}>
              {sites.length === 0 ? (t('bins.emptyNoSites') || 'Kuka hozzáadása előtt legalább egy telephelyet fel kell venni.') : t('bins.empty')}
            </p>
          )}
        </div>
      )}
      {activeTab === 'details' && (
        <div>
          {/* Header with identifier */}
          <div style={{ fontSize: '1.4rem', fontWeight: 600, color: '#111', marginTop: 4 }}>
            {selectedBin?.details?.bin_identifier || '—'}
          </div>
          <div style={{ height: 3, background: '#1976d2', width: 120, margin: '10px 0 18px' }} />
          {/* Two column layout */}
          <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 60%', minWidth: 340 }}>
              <div style={{ marginBottom: 16 }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 6, flexWrap:'wrap', gap:6 }}>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{t('bins.map')}</div>
                  {canEditPosition && (
                    <div style={{ display:'flex', gap:6, alignItems:'center', flexWrap:'wrap' }}>
                      {!positionEditMode ? (
                        <button
                          type="button"
                          className="btn btn-sm"
                          disabled={!selectedBin?.details?.location?.coordinates}
                          title={!selectedBin?.details?.location?.coordinates ? (t('bins.details.noCoordinates') || 'No coordinates') : undefined}
                          onClick={beginPositionEdit}
                          style={{ background:'#1976d2', color:'#fff', fontSize:11, padding:'4px 10px', border:'none', borderRadius:4, opacity: !selectedBin?.details?.location?.coordinates ? 0.5 : 1 }}
                        >{t('bins.details.editPosition') || 'Pozíció mozgatása'}</button>
                      ) : (
                        <>
                          <span style={{ fontSize:11, opacity:.65 }}>{t('bins.details.dragHint') || 'Húzd a markert az új helyre'}</span>
                          <button
                            type="button"
                            className="btn btn-sm"
                            disabled={savingPosition}
                            onClick={savePositionEdit}
                            style={{ background:'#2e7d32', color:'#fff', fontSize:11, padding:'4px 10px', border:'none', borderRadius:4 }}
                          >{savingPosition ? (t('common.saving') || 'Mentés...') : (t('common.save') || 'Mentés')}</button>
                          <button
                            type="button"
                            className="btn btn-sm"
                            disabled={savingPosition}
                            onClick={cancelPositionEdit}
                            style={{ background:'#757575', color:'#fff', fontSize:11, padding:'4px 10px', border:'none', borderRadius:4 }}
                          >{t('common.cancel') || 'Mégse'}</button>
                        </>
                      )}
                    </div>
                  )}
                </div>
                <div style={{ height: 220, border: '1px solid #ccc', borderRadius: 6, overflow: 'hidden', position: 'relative' }}>
                  <SingleBinMap
                    bin={selectedBin}
                    editMode={positionEditMode}
                    draftPosition={draftPosition}
                    onDragEnd={setDraftPosition}
                  />
                </div>
                {positionError && <div style={{ fontSize:11, color:'#c62828', marginTop:6 }}>{positionError}</div>}
                {positionEditMode && draftPosition && (
                  <div style={{ fontSize:11, opacity:.6, marginTop:4, fontFamily:'monospace' }}>
                    {draftPosition.lat.toFixed(6)}, {draftPosition.lng.toFixed(6)}
                  </div>
                )}
              </div>
              {/* Manual coordinate editing removed as requested */}
            </div>
            <div style={{ flex: '1 1 40%', minWidth: 260 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                {renderStatCard(t('bins.details.stats.filling'),
                  selectedBin?.snapshot?.fill_level != null ? selectedBin.snapshot.fill_level + '%' :
                  selectedBin?.latest_telemetry?.fill_level != null ? selectedBin.latest_telemetry.fill_level + '%' : '—', '🗳️')}
                {(() => {
                  // Custom door card with latch toggle integrated
                  const ds = selectedBin?.snapshot?.door_status || selectedBin?.latest_telemetry?.door_status;
                  const currentLatchOpen = (() => {
                    const snap = selectedBin?.snapshot || selectedBin?.latest_telemetry;
                    if (snap?.latch_state != null) return snap.latch_state === 0; // 0=open
                    const stat = snap?.door_status;
                    if (stat) {
                      const mainState = stat.main || stat.state || '';
                      const latchField = stat.latch || '';
                      return /open/i.test(mainState) || /unlocked|open/i.test(latchField);
                    }
                    return false;
                  })();
                  const summary = currentLatchOpen ? (t('bins.latch.open') || 'Nyitva') : (t('bins.latch.closed') || 'Zárva');
                  const toggleLatch = async () => {
                    if (!selectedBin?.details?.bin_id) return;
                    const binId = selectedBin.details.bin_id;
                    setLatchSending(true);
                    // Clear any previous pending ack
                    if (latchAwaitingAckRef.current) {
                      clearTimeout(latchAwaitingAckRef.current.timeoutId);
                      latchAwaitingAckRef.current = null;
                    }
                    try {
                      await binService.setLatch(binId, !currentLatchOpen);
                      // Keep button disabled — wait for device ack via WebSocket (metric=latch)
                      // Fallback: re-enable after 10 s if no ack arrives
                      const timeoutId = setTimeout(() => {
                        if (latchAwaitingAckRef.current?.bin_id === binId) {
                          latchAwaitingAckRef.current = null;
                          setLatchSending(false);
                        }
                      }, 10000);
                      latchAwaitingAckRef.current = { bin_id: binId, timeoutId };
                      // Optimistic UI update: set latch_state = 0 (open) or 1 (close)
                      const newLatchState = currentLatchOpen ? 1 : 0;
                      setSelectedBin(prev => {
                        if (!prev) return prev;
                        const snap = { ...(prev.snapshot || prev.latest_telemetry || {}), latch_state: newLatchState };
                        return { ...prev, snapshot: snap, latest_telemetry: snap };
                      });
                      setBins(prev => prev.map(b => b.bin_id === binId ? { ...b, latest_telemetry: { ...(b.latest_telemetry || {}), latch_state: newLatchState } } : b));
                    } catch(e) {
                      console.error('Latch command failed', e);
                      latchAwaitingAckRef.current = null;
                      setLatchSending(false);
                    }
                  };
                  return (
                    <div style={{ flex: '1 1 110px', minWidth:110, background:'#fff', border:'1px solid #ddd', borderRadius:8, padding:'10px 12px', display:'flex', flexDirection:'column', gap:6 }}>
                      <div style={{ fontSize:12, fontWeight:600, display:'flex', alignItems:'center', gap:6 }}><span>🔐</span><span>{t('bins.tooltip.door')}</span></div>
                      <div style={{ fontSize:15, fontWeight:600, color: currentLatchOpen ? '#2e7d32' : '#c62828' }}>{summary || '—'}</div>
                      {/* Removed checkbox per request; show only state label and command button */}
                      <button type="button" className="btn btn-sm" disabled={latchSending} onClick={toggleLatch} style={{ background:'#1976d2', color:'#fff', padding:'4px 8px', fontSize:11, border:'none', borderRadius:4 }}>
                        {latchSending ? (t('bins.latch.sending') || 'Küldés...') : currentLatchOpen ? (t('bins.latch.sendClose') || 'Zárás küldése') : (t('bins.latch.sendOpen') || 'Nyitás küldése')}
                      </button>
                    </div>
                  );
                })()}
                {renderStatCard(t('bins.tooltip.batteryVolts'),
                  (() => {
                    const mv = selectedBin?.snapshot?.battery_level ?? selectedBin?.latest_telemetry?.battery_level;
                    if (mv == null) return '—';
                    const val = Number(mv);
                    if (isNaN(val)) return '—';
                    if (val > 16800) return 'Hibás adat';
                    const v = val/1000;
                    const lowV = Number(getCachedThresholds().battery_low_threshold_v || 11);
                    const low = v < lowV;
                    return (
                      <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                        <div>{v.toFixed(1)}</div>
                        {low && <div style={{ color:'#c62828', fontSize:11, fontWeight:600 }}>Gyenge!</div>}
                      </div>
                    );
                  })(), '🔋')}
                {renderStatCard(t('bins.tooltip.lastHeartbeat'), (() => {
                  const ts = selectedBin?.snapshot?.time || selectedBin?.latest_telemetry?.last_heartbeat;
                  if (!ts) return '—';
                  try { const d = new Date(ts); return d.toLocaleDateString() + ' ' + d.toLocaleTimeString(); } catch { return ts; }
                })(), '�')}
              </div>
              {/* Removed separate latch block; integrated into door stat card above */}
            </div>
          </div>
          {/* Inner Tabs */}
          <div style={{ marginTop: 28 }}>
            <div className="tab-buttons" style={{ marginBottom: 0 }}>
              {['details','chart'].map(key => (
                <button
                  key={key}
                  type="button"
                  onClick={()=> setInnerTab(key)}
                  className={`btn tab-button ${innerTab === key ? 'active' : ''}`}
                  style={{ fontSize: 13 }}
                >{key === 'details' ? (t('bins.details.innerTabs.details') || 'Részletek') : (t('bins.details.innerTabs.chart') || 'Grafikon')}</button>
              ))}
            </div>
            <div style={{ background: '#fff', border: '1px solid #ddd', borderTop: 'none', borderRadius: '0 0 6px 6px', padding: '16px 18px', marginTop: 0 }}>
              {innerTab === 'details' && (
                <div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px', fontSize: 14 }}>
                    {/* Bal oszlop: Név, Cím, Pozíció */}
                    <div style={{ display:'flex', flexDirection:'column', gap: '12px 24px' }}>
                      {renderField(t('bins.details.fields.name'), selectedBin?.details?.bin_identifier || '—')}
                      {(() => {
                        const addr = selectedBin?.details?.address;
                        const hasAddress = !!(addr && String(addr).trim());
                        const val = hasAddress ? String(addr).trim() : (<span style={{ textDecoration:'line-through', opacity:.6 }}>—</span>);
                        return renderField(t('common.address') || 'Cím', val);
                      })()}
                      {renderField(t('bins.details.fields.location'), selectedBin?.details?.location?.coordinates ? (selectedBin.details.location.coordinates[1] + ', ' + selectedBin.details.location.coordinates[0]) : '—')}
                    </div>
                    {/* Jobb oszlop: Telepítés dátuma, Utolsó kapcsolat, Utolsó ürítés, Napszak */}
                    <div style={{ display:'flex', flexDirection:'column', gap: '12px 24px' }}>
                      {renderField(t('bins.details.fields.installationDate'), selectedBin?.details?.created_at ? formatDate(selectedBin.details.created_at) : '—')}
                      {renderField(t('bins.details.fields.lastContact'), selectedBin?.snapshot?.time ? formatDate(selectedBin.snapshot.time) : selectedBin?.latest_telemetry?.time ? formatDate(selectedBin.latest_telemetry.time) : '—')}
                      {renderField(t('bins.details.fields.lastEmptied'), selectedBin?.details?.last_emptied ? formatDate(selectedBin.details.last_emptied) : '—')}
                      {(() => {
                        const det = selectedBin?.details;
                        if (!det) return null;
                        const binCoords = det.location?.coordinates;
                        const siteCoords = det.site_location?.coordinates;
                        let lat, lon;
                        if (binCoords) { [lon, lat] = binCoords; }
                        else if (siteCoords) { [lon, lat] = siteCoords; }
                        else { lat = 47.4979; lon = 19.0402; }
                        const now = new Date();
                        const times = SunCalc.getTimes(now, lat, lon);
                        const { sunrise, sunset } = times;
                        const validTimes = !isNaN(sunrise.getTime()) && !isNaN(sunset.getTime());
                        const isNight = validTimes ? (now < sunrise || now > sunset) : true;
                        const icon = isNight ? '🌙' : '☀️';
                        const label = isNight ? 'Éjjel — Háttérvilágítás BE' : 'Nappal — Háttérvilágítás KI';
                        const timeRange = validTimes
                          ? ` (${sunrise.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}–${sunset.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })})`
                          : '';
                        return renderField('Napszak', <span>{label}{timeRange}&nbsp;{icon}</span>);
                      })()}
                    </div>
                  </div>
                </div>
              )}
              {innerTab === 'chart' && (
                <div style={{ fontSize: 13 }}>
                  {/* Chart sub-tabs and range selector */}
                  <div style={{ marginBottom:12, display:'flex', gap:8, alignItems:'center', justifyContent:'space-between' }}>
                    {[
                      { key:'compression', label: 'Tömörítési ciklusok' },
                      { key:'empty', label: 'Ürítési ciklusok' },
                      { key:'battery', label: 'Akkumulátor feszültség' },
                      { key:'temperature', label: 'Hőmérséklet' },
                      { key:'signal', label: 'Jelerősség' },
                    ].map(ct => (
                      <button key={ct.key} type="button" onClick={()=> setChartTab(ct.key)} className={`btn btn-sm ${chartTab===ct.key?'btn-primary':'btn-secondary'}`}>{ct.label}</button>
                    ))}
                    {(chartTab === 'battery' || chartTab === 'temperature' || chartTab === 'signal') && (
                      <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                        <span style={{ fontSize:12, opacity:.7 }}>Időtartam:</span>
                        <button type="button" className={`btn btn-sm ${chartRange==='day'?'btn-primary':'btn-secondary'}`} onClick={()=> setChartRange('day')}>Utolsó 1 nap</button>
                        <button type="button" className={`btn btn-sm ${chartRange==='week'?'btn-primary':'btn-secondary'}`} onClick={()=> setChartRange('week')}>Utolsó 7 nap</button>
                        <button type="button" className={`btn btn-sm ${chartRange==='month'?'btn-primary':'btn-secondary'}`} onClick={()=> setChartRange('month')}>Utolsó 30 nap</button>
                      </div>
                    )}
                  </div>
                  <div style={{ minHeight:260, border:'1px solid #ddd', borderRadius:6, padding:16, background:'#fff' }}>
                    <div style={{ fontSize:14, fontWeight:600, marginBottom:12 }}>
                      {chartTab === 'compression' && 'Tömörítési ciklusok'}
                      {chartTab === 'empty' && 'Ürítési ciklusok'}
                      {chartTab === 'battery' && 'Akkumulátor feszültség'}
                      {chartTab === 'temperature' && 'Hőmérséklet'}
                      {chartTab === 'signal' && 'Jelerősség'}
                    </div>
                    {chartTab === 'battery' && (() => {
                      const telem = selectedBin?.telemetry || [];
                      const points = [];
                      for (const row of telem) {
                        const mvRaw = row.battery_level;
                        if (mvRaw == null) continue;
                        const ts = row.time || row.created_at || row.timestamp;
                        const tSec = toEpochSeconds(ts);
                        if (tSec == null) continue;
                        const mv = Number(mvRaw);
                        if (isNaN(mv)) continue;
                        points.push({ t: tSec, mv });
                      }
                      points.sort((a, b) => a.t - b.t);
                      if (points.length === 0) {
                        return <div style={{ fontSize:12, opacity:.65 }}>Nincs akkumulátor adat.</div>;
                      }
                      const x = points.map(p => p.t);
                      const yValid = points.map(p => (p.mv > 16800 ? null : Number((p.mv / 1000).toFixed(3))));
                      const yInvalid = points.map(p => (p.mv > 16800 ? Number((p.mv / 1000).toFixed(3)) : null));
                      const yThreshold = points.map(() => 11);
                      const data = [x, yValid, yInvalid, yThreshold];
                      return <div style={{ height:200 }}><UPlotChart data={data} options={batteryOptions} height={200} /></div>;
                    })()}
                    {chartTab === 'temperature' && (() => {
                      const telem = selectedBin?.telemetry || [];
                      const points = [];
                      for (const row of telem) {
                        const tempRaw = row.temperature;
                        if (tempRaw == null) continue;
                        const ts = row.time || row.created_at || row.timestamp;
                        const tSec = toEpochSeconds(ts);
                        if (tSec == null) continue;
                        const val = Number(tempRaw);
                        if (isNaN(val)) continue;
                        points.push({ t: tSec, v: Number(val.toFixed(2)) });
                      }
                      points.sort((a, b) => a.t - b.t);
                      if (points.length === 0) {
                        return <div style={{ fontSize:12, opacity:.65 }}>Nincs hőmérséklet adat.</div>;
                      }
                      const x = points.map(p => p.t);
                      const y = points.map(p => p.v);
                      const data = [x, y];
                      return <div style={{ height:200 }}><UPlotChart data={data} options={temperatureOptions} height={200} /></div>;
                    })()}
                    {chartTab === 'signal' && (() => {
                      const telem = selectedBin?.telemetry || [];
                      const points = [];
                      for (const row of telem) {
                        const rssiRaw = row.rssi != null ? row.rssi : row.signal?.rssi;
                        if (rssiRaw == null) continue;
                        const ts = row.time || row.created_at || row.timestamp;
                        const tSec = toEpochSeconds(ts);
                        if (tSec == null) continue;
                        const val = Number(rssiRaw);
                        if (isNaN(val)) continue;
                        points.push({ t: tSec, v: Number(val.toFixed(2)) });
                      }
                      points.sort((a, b) => a.t - b.t);
                      if (points.length === 0) {
                        return <div style={{ fontSize:12, opacity:.65 }}>Nincs jeladat.</div>;
                      }
                      const x = points.map(p => p.t);
                      const y = points.map(p => p.v);
                      const data = [x, y];
                      return <div style={{ height:200 }}><UPlotChart data={data} options={signalOptions} height={200} /></div>;
                    })()}
                    {(chartTab !== 'battery' && chartTab !== 'temperature' && chartTab !== 'signal') && <div style={{ fontSize:12, opacity:.65 }}>Diagram fejlesztés alatt ehhez a fülhöz.</div>}
                  </div>
                </div>
              )}
            </div>
          </div>
          {detailsLoading && <div style={{ marginTop: 12 }}>{t('logs.loading') || 'Loading...'}</div>}
          {detailsError && <div className="alert alert-danger" style={{ marginTop: 12 }}>{detailsError}</div>}
        </div>
      )}
      <Modal
        open={confirmState.open}
        title={confirmState.mode === 'delete' ? (t('confirmBin.titleDelete') || t('confirm.titleDelete')) : (t('confirmBin.titleRestore') || t('confirm.titleRestore'))}
        onClose={() => !confirmState.loading && setConfirmState({ open:false, mode:null, bin:null, loading:false })}
        actions={
          <>
            <button className="btn btn-secondary" disabled={confirmState.loading} onClick={() => setConfirmState({ open:false, mode:null, bin:null, loading:false })}>{t('confirmBin.cancel') || t('confirm.cancel')}</button>
            <button className={confirmState.mode === 'delete' ? 'btn btn-danger' : 'btn btn-primary'} disabled={confirmState.loading} onClick={performBinConfirm}>{confirmState.loading ? '...' : (t('confirmBin.proceed') || t('confirm.proceed'))}</button>
          </>
        }
      >
        <p style={{ marginTop:0 }}>
          {confirmState.mode === 'delete' ? (t('confirmBin.messageDelete') || t('confirm.messageDelete')) : (t('confirmBin.messageRestore') || t('confirm.messageRestore'))}
        </p>
        {confirmState.bin && (
          <div style={{ fontSize:'.75rem', opacity:.7, marginTop:12 }}>
            ID: {confirmState.bin.bin_id} • {confirmState.bin.bin_identifier}
          </div>
        )}
      </Modal>
      {/* Collection list modal */}
      <Modal
        open={collectionModal.open}
        title={t('bins.collectionListTitle') || 'Nyomtatható ürítési lista'}
        onClose={()=> setCollectionModal(m => ({ ...m, open:false }))}
        actions={
          <>
            <button className="btn btn-secondary" onClick={()=> setCollectionModal(m => ({ ...m, open:false }))}>{t('common.cancel') || 'Mégse'}</button>
            <button
              className="btn btn-primary"
              disabled={collectionModal.loading}
              onClick={() => {
                const siteId = collectionModal.siteId || filterSiteId;
                if(!siteId) return;
                const q = new URLSearchParams();
                if(collectionModal.minFill!=null) q.set('min_fill', collectionModal.minFill);
                if(collectionModal.sort) q.set('sort', collectionModal.sort);
                if(collectionModal.includeUnordered) q.set('include_unordered','1');
                // updated_within param temporarily disabled
                q.set('format','html');
                // Use window.open to open the URL in a new tab with the query parameters
                const url = `${window.location.origin}/api/sites/${siteId}/collection-list?${q.toString()}`;
                const win = window.open(url, '_blank');
                if(!win) {
                  alert('Popup blokkolva. Engedélyezd a felugró ablakokat erre az oldalra.');
                }
              }}
            >{t('bins.collectionListGenerate') || 'Megnyitás'}</button>
          </>
        }
      >
        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
          <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
            <div style={{ display:'flex', flexDirection:'column', minWidth:110 }}>
              <label style={{ fontSize:11, fontWeight:600 }}>Min %</label>
              <input type="number" className="form-control" value={collectionModal.minFill}
                onChange={e=> setCollectionModal(m => ({ ...m, minFill: Math.min(100, Math.max(0, parseInt(e.target.value||'0',10))) }))} />
            </div>
            <div style={{ display:'flex', flexDirection:'column', minWidth:140 }}>
              <label style={{ fontSize:11, fontWeight:600 }}>{t('common.sort') || 'Rendezés'}</label>
              <select className="form-control" value={collectionModal.sort} onChange={e=> setCollectionModal(m => ({ ...m, sort:e.target.value }))}>
                <option value="order">{t('bins.collectionListSort.order') || 'Telep. sorrend'}</option>
                <option value="fill">{t('bins.collectionListSort.fill') || 'Telítettség'}</option>
                <option value="mixed">{t('bins.collectionListSort.mixed') || 'Vegyes'}</option>
              </select>
            </div>
            {/* Friss (perc) mező ideiglenesen eltávolítva */}
            <div style={{ display:'flex', alignItems:'center', gap:6, marginTop:18 }}>
              <input type="checkbox" checked={collectionModal.includeUnordered} onChange={e=> setCollectionModal(m => ({ ...m, includeUnordered: e.target.checked }))} />
              <span style={{ fontSize:12 }}>{t('bins.collectionListIncludeUnordered') || 'Sorrend nélküli is'}</span>
            </div>
          </div>
          <div style={{ fontSize:11, opacity:.6 }}>
            {t('bins.collectionListHint') || 'A Megnyitás gomb új lapon HTML formában jeleníti meg, ahonnan nyomtatható.'}
          </div>
        </div>
      </Modal>
      {/* Maintenance list modal */}
      <Modal
        open={maintenanceModal.open}
        title={t('bins.maintenanceListTitle') || 'Nyomtatható karbantartási lista'}
        onClose={()=> setMaintenanceModal(m => ({ ...m, open:false }))}
        actions={
          <>
            <button className="btn btn-secondary" onClick={()=> setMaintenanceModal(m => ({ ...m, open:false }))}>{t('common.cancel') || 'Mégse'}</button>
            <button
              className="btn btn-primary"
              disabled={maintenanceModal.loading}
              onClick={() => {
                const siteId = maintenanceModal.siteId || filterSiteId;
                if(!siteId) return;
                const q = new URLSearchParams();
                if(maintenanceModal.minV!=null) q.set('min_v', maintenanceModal.minV);
                if(maintenanceModal.sort) q.set('sort', maintenanceModal.sort);
                if(maintenanceModal.includeUnordered) q.set('include_unordered','1');
                q.set('format','html');
                const url = `${window.location.origin}/api/sites/${siteId}/maintenance-list?${q.toString()}`;
                const win = window.open(url, '_blank');
                if(!win) {
                  alert('Popup blokkolva. Engedélyezd a felugró ablakokat erre az oldalra.');
                }
              }}
            >{t('bins.maintenanceListGenerate') || 'Megnyitás'}</button>
          </>
        }
      >
        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
          <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
            <div style={{ display:'flex', flexDirection:'column', minWidth:110 }}>
              <label style={{ fontSize:11, fontWeight:600 }}>Min V</label>
              <input type="number" step="0.1" className="form-control" value={maintenanceModal.minV}
                onChange={e=> setMaintenanceModal(m => ({ ...m, minV: Math.min(16, Math.max(0, parseFloat(e.target.value||'0'))) }))} />
            </div>
            <div style={{ display:'flex', flexDirection:'column', minWidth:140 }}>
              <label style={{ fontSize:11, fontWeight:600 }}>{t('common.sort') || 'Rendezés'}</label>
              <select className="form-control" value={maintenanceModal.sort} onChange={e=> setMaintenanceModal(m => ({ ...m, sort:e.target.value }))}>
                <option value="order">{t('bins.collectionListSort.order') || 'Telep. sorrend'}</option>
                <option value="battery">{t('bins.maintenanceListSort.battery') || 'Akkufeszültség'}</option>
              </select>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:6, marginTop:18 }}>
              <input type="checkbox" checked={maintenanceModal.includeUnordered} onChange={e=> setMaintenanceModal(m => ({ ...m, includeUnordered: e.target.checked }))} />
              <span style={{ fontSize:12 }}>{t('bins.collectionListIncludeUnordered') || 'Sorrend nélküli is'}</span>
            </div>
          </div>
          <div style={{ fontSize:11, opacity:.6 }}>
            {t('bins.maintenanceListHint') || 'A Megnyitás gomb új lapon HTML formában jeleníti meg, ahonnan nyomtatható.'}
          </div>
        </div>
      </Modal>
      {/* Portal suggestions for inline edit */}
      {suggestPos && editSuggestions.length > 0 && !suppressBinEditSuggestions && createPortal(
        <div
          style={{ position:'absolute', top:0, left:0, width:'0', height:'0', zIndex:10000 }}
          aria-label="geo-suggestions-portal"
        >
          {/* Backdrop to intercept clicks (not scrolling) */}
          <div
            onMouseDown={(e)=> { e.stopPropagation(); e.preventDefault(); setEditSuggestions([]); setSuggestPos(null); }}
            style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0)', cursor:'default' }}
          />
          <div
            style={{
              position:'absolute',
              top:suggestPos.top,
              left:suggestPos.left,
              width:suggestPos.width,
              background:'#fff',
              border:'1px solid #666',
              borderRadius:4,
              boxShadow:'0 8px 24px rgba(0,0,0,0.35)',
              maxHeight:300,
              overflowY:'auto',
              overscrollBehavior:'contain',
              padding:'4px 0'
            }}
            onMouseDown={e=> e.stopPropagation()}
            onClick={e=> e.stopPropagation()}
          >
            {editSuggestions.map((sug,i) => {
              const formatted = sug.formatted || sug.display || '';
              const active = i === highlightIdx;
              return (
                <div
                  key={i}
                  style={{
                    padding:'8px 12px',
                    fontSize:12,
                    cursor:'pointer',
                    background: active ? '#e3f2fd' : '#fff',
                    borderBottom: i < editSuggestions.length-1 ? '1px solid #e0e0e0' : 'none',
                    lineHeight:1.25
                  }}
                  onMouseEnter={()=> setHighlightIdx(i)}
                  onMouseLeave={()=> setHighlightIdx(-1)}
                  onMouseDown={(ev)=> {
                    ev.preventDefault();
                    const chosen = editSuggestions[i];
                    const f = chosen.formatted || chosen.display || '';
                    onEditFieldChange('address', f);
                    if(chosen.latitude && chosen.longitude){
                      onEditFieldChange('latitude', String(chosen.latitude));
                      onEditFieldChange('longitude', String(chosen.longitude));
                    }
                    setSuppressBinEditSuggestions(true);
                    setTimeout(()=> setSuppressBinEditSuggestions(false), 400);
                    setEditSuggestions([]); setSuggestPos(null);
                  }}
                >
                  {formatted || sug.display}
                  {sug._notable && <span title={t('geocode.notableHint') || 'Nevezetes találat'} className="geo-notable-dot" style={{ marginLeft:6 }} />}
                </div>
              );
            })}
            {editSuggestions.length === 0 && (
              <div style={{ padding:'10px 12px', fontSize:12, opacity:.7 }}>No results</div>
            )}
          </div>
        </div>, document.body)
      }
    </div>
  );
};

function tabStyle(active) {
  return {
    padding: '8px 16px',
    background: active ? '#1976d2' : '#f3f3f3',
    color: active ? '#fff' : '#333',
    border: '1px solid #ccc',
    borderBottom: active ? '2px solid #1976d2' : '2px solid transparent',
    borderRadius: '4px 4px 0 0',
    cursor: 'pointer',
    fontWeight: active ? 600 : 400,
  };
}

export default BinsPage;

function formatDate(ts){
  if(!ts) return '—';
  try { return new Date(ts).toLocaleString(); } catch(e){ return ts; }
}

function renderStatCard(label, value, icon){
  return (
    <div style={{ flex: '1 1 110px', minWidth: 110, background: '#fff', border: '1px solid #ddd', borderRadius: 8, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}><span>{icon}</span><span>{label}</span></div>
      <div style={{ fontSize: 16, fontWeight: 600 }}>{value}</div>
    </div>
  );
}

function renderField(label, value){
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', opacity: .85 }}>{label}</div>
      <div style={{ fontSize: 14 }}>{value}</div>
    </div>
  );
}

function formatShortTime(ts){
  if(!ts) return '—';
  try {
    const d = new Date(ts);
    const now = Date.now();
    const diffMs = now - d.getTime();
    if (diffMs < 0) return d.toLocaleTimeString();
    const diffSec = Math.floor(diffMs/1000);
    if (diffSec < 60) return diffSec + 's';
    const diffMin = Math.floor(diffSec/60);
    if (diffMin < 60) return diffMin + 'm';
    const diffH = Math.floor(diffMin/60);
    if (diffH < 24) return diffH + 'h';
    const diffD = Math.floor(diffH/24);
    return diffD + 'd';
  } catch { return '—'; }
}